import { useState, useRef, useCallback, useEffect } from "react";
import { PublicClientApplication } from "@azure/msal-browser";

const msalConfig = {
  auth: {
    clientId: "fe4b9649-04b9-4ac6-84db-ceefa4a5773a",
    authority: "https://login.microsoftonline.com/8a9b61ce-3f95-490f-80e8-7a0b01a1261a",
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: false },
};

const msalInstance = new PublicClientApplication(msalConfig);
const loginRequest = { scopes: ["User.Read"] };
const Fonts = () => <link href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />;

const CONSENT_VERSION = "1.0";
const CONSENT_FUNCTION_URL = process.env.REACT_APP_CONSENT_FUNCTION_URL;
const SAVE_TEST_URL = process.env.REACT_APP_SAVE_TEST_URL;
const GET_TEST_URL = process.env.REACT_APP_GET_TEST_URL;
const SEND_REPORT_URL = process.env.REACT_APP_SEND_REPORT_URL;
const MARK_USED_URL = process.env.REACT_APP_MARK_USED_URL;
const SPEECH_TOKEN_URL = process.env.REACT_APP_SPEECH_TOKEN_URL;
const ANALYZE_URL = process.env.REACT_APP_ANALYZE_URL;
const AXEPTA_TENANT_ID = "8a9b61ce-3f95-490f-80e8-7a0b01a1261a";
const LOGO_URL = "https://linguacheckstorage.blob.core.windows.net/linguacheckstorage/axepta-logo.png";

const WRITING_TIME_FIRST = 240;
const WRITING_TIME = 180;
const SPEAKING_READ_TIME = 30;
const SPEAKING_RECORD_TIME = 60;
const LISTENING_SUMMARY_TIME = 300;

const GENERATING_STEPS = [
  { icon: "🔍", text: "Searching company website..." },
  { icon: "🏢", text: "Reading company values & culture..." },
  { icon: "✍️", text: "Crafting writing prompts..." },
  { icon: "🎙", text: "Creating speaking tasks..." },
  { icon: "👂", text: "Building listening passage..." },
  { icon: "🔗", text: "Almost ready..." },
];

const LANG_META = {
  en: { label: "English", flag: "🇬🇧", voiceLang: "en-GB", azureLang: "en-GB" },
  fr: { label: "Français", flag: "🇫🇷", voiceLang: "fr-FR", azureLang: "fr-FR" },
  de: { label: "Deutsch", flag: "🇩🇪", voiceLang: "de-DE", azureLang: "de-DE" },
};

const CONSENT_TEXT = {
  en: "The responses I provide during this assessment (written, spoken and listening) are processed solely for the purpose of evaluating my language proficiency for this job application. No personal data from my assessment responses is stored after analysis is complete. Only a record of my consent and the assessment report are retained by the recruiting organisation. I have the right to withdraw consent at any time by emailing dpo@axepta.com",
  fr: "Les réponses que je fournis lors de cette évaluation (écrites, orales et de compréhension) sont traitées uniquement dans le but d'évaluer mes compétences linguistiques pour cette candidature. Aucune donnée personnelle issue de mes réponses n'est conservée après l'analyse. Seul un enregistrement de mon consentement et le rapport d'évaluation sont conservés par l'organisation recruteuse. J'ai le droit de retirer mon consentement à tout moment en envoyant un e-mail à dpo@axepta.com",
  de: "Die Antworten, die ich während dieser Bewertung gebe (schriftlich, mündlich und Hörverständnis), werden ausschließlich zum Zweck der Beurteilung meiner Sprachkenntnisse für diese Bewerbung verarbeitet. Nach Abschluss der Analyse werden keine personenbezogenen Daten aus meinen Antworten gespeichert. Nur eine Aufzeichnung meiner Einwilligung und der Bewertungsbericht werden von der rekrutierenden Organisation aufbewahrt. Ich habe das Recht, meine Einwilligung jederzeit zu widerrufen, indem ich eine E-Mail an dpo@axepta.com sende",
};

const CONSENT_UI = {
  en: { title: "Data Privacy & Consent", subtitle: "Please read our data privacy statement and confirm your consent before starting your assessment.", nameLabel: "Your Full Name", emailLabel: "Your Email Address", namePlaceholder: "e.g. Sarah Johnson", emailPlaceholder: "e.g. sarah@email.com", privacyTitle: "🔒 Data Privacy Statement", checkboxText: "I have read and understood the data privacy statement above and I consent to my responses being processed for the purpose of this language assessment.", continueBtn: "Start Assessment →", submittingBtn: "Recording consent...", errorMsg: "There was a problem recording your consent. Please try again or contact dpo@axepta.com", footer: "Your consent is recorded with a timestamp for compliance purposes.", withdraw: "To withdraw consent at any time, email" },
  fr: { title: "Confidentialité et consentement", subtitle: "Veuillez lire notre déclaration de confidentialité et confirmer votre consentement avant de commencer votre évaluation.", nameLabel: "Votre nom complet", emailLabel: "Votre adresse e-mail", namePlaceholder: "ex. Sarah Johnson", emailPlaceholder: "ex. sarah@email.com", privacyTitle: "🔒 Déclaration de confidentialité", checkboxText: "J'ai lu et compris la déclaration de confidentialité ci-dessus et je consens à ce que mes réponses soient traitées aux fins de cette évaluation linguistique.", continueBtn: "Commencer l'évaluation →", submittingBtn: "Enregistrement du consentement...", errorMsg: "Un problème est survenu lors de l'enregistrement de votre consentement. Veuillez réessayer ou contacter dpo@axepta.com", footer: "Votre consentement est enregistré avec un horodatage à des fins de conformité.", withdraw: "Pour retirer votre consentement à tout moment, envoyez un e-mail à" },
  de: { title: "Datenschutz & Einwilligung", subtitle: "Bitte lesen Sie unsere Datenschutzerklärung und bestätigen Sie Ihre Einwilligung, bevor Sie mit der Bewertung beginnen.", nameLabel: "Ihr vollständiger Name", emailLabel: "Ihre E-Mail-Adresse", namePlaceholder: "z.B. Sarah Johnson", emailPlaceholder: "z.B. sarah@email.com", privacyTitle: "🔒 Datenschutzerklärung", checkboxText: "Ich habe die obige Datenschutzerklärung gelesen und verstanden und stimme zu, dass meine Antworten zum Zweck dieser Sprachbewertung verarbeitet werden.", continueBtn: "Bewertung starten →", submittingBtn: "Einwilligung wird gespeichert...", errorMsg: "Bei der Aufzeichnung Ihrer Einwilligung ist ein Problem aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie dpo@axepta.com", footer: "Ihre Einwilligung wird zu Compliance-Zwecken mit einem Zeitstempel aufgezeichnet.", withdraw: "Um Ihre Einwilligung jederzeit zu widerrufen, senden Sie eine E-Mail an" },
};

const WELCOME_UI = {
  en: { hi: "Hi", subtitle: "You're taking a language assessment for the", position: "position.", order: "You will complete one full assessment per language, in order:", writing: "Writing", writingDesc: "4 min first, 3 min after", speaking: "Speaking", speakingDesc: "30s read + 60s voice", listening: "Listening", listeningDesc: "Audio + 5 min summary", time: "Estimated time", minutes: "minutes", quiet: "Find a quiet spot and allow microphone access when prompted. Copy-paste is disabled.", startBtn: "Check My Device →", beforeWeBegin: "Before we begin, let's make sure your device is ready.", aiDisclaimer: "This assessment is evaluated by AI and the results are intended for informational purposes only. They form part of a broader recruitment process and further assessments or interviews may be required before any hiring decision is made." },
  fr: { hi: "Bonjour", subtitle: "Vous passez une évaluation linguistique pour le poste de", position: ".", order: "Vous compléterez une évaluation complète par langue, dans l'ordre suivant :", writing: "Expression écrite", writingDesc: "4 min première, 3 min ensuite", speaking: "Expression orale", speakingDesc: "30s lecture + 60s voix", listening: "Compréhension orale", listeningDesc: "Audio + résumé 5 min", time: "Durée estimée", minutes: "minutes", quiet: "Trouvez un endroit calme. Le copier-coller est désactivé.", startBtn: "Vérifier mon appareil →", beforeWeBegin: "Avant de commencer, vérifions que votre appareil est prêt.", aiDisclaimer: "Cette évaluation est analysée par une intelligence artificielle et les résultats sont fournis à titre informatif uniquement. Ils s'inscrivent dans le cadre d'un processus de recrutement plus large et des évaluations ou entretiens supplémentaires pourront être requis avant toute décision d'embauche." },
  de: { hi: "Hallo", subtitle: "Sie nehmen an einer Sprachbewertung für die Stelle als", position: "teil.", order: "Sie absolvieren der Reihe nach eine vollständige Bewertung pro Sprache:", writing: "Schreiben", writingDesc: "4 Min. erste, 3 Min. danach", speaking: "Sprechen", speakingDesc: "30s lesen + 60s Sprache", listening: "Hören", listeningDesc: "Audio + 5 Min. Zusammenfassung", time: "Geschätzte Zeit", minutes: "Minuten", quiet: "Suchen Sie einen ruhigen Platz. Kopieren und Einfügen ist deaktiviert.", startBtn: "Gerät prüfen →", beforeWeBegin: "Bevor wir beginnen, stellen wir sicher, dass Ihr Gerät bereit ist.", aiDisclaimer: "Diese Bewertung wird durch künstliche Intelligenz ausgewertet und die Ergebnisse dienen ausschließlich zu Informationszwecken. Sie sind Teil eines umfassenderen Einstellungsverfahrens, und weitere Beurteilungen oder Vorstellungsgespräche können vor einer Einstellungsentscheidung erforderlich sein." },
};

const THANKYOU_UI = {
  en: { title: "Thank you!", subtitle: "Your assessment is complete.", message: "Your responses have been submitted successfully. Our team will review the results and your recruiter will be in touch with you as soon as possible.", note: "Results are confidential and will not be shared with you directly." },
  fr: { title: "Merci !", subtitle: "Votre évaluation est terminée.", message: "Vos réponses ont été soumises avec succès. Notre équipe examinera les résultats et votre recruteur vous contactera dans les meilleurs délais.", note: "Les résultats sont confidentiels et ne vous seront pas communiqués directement." },
  de: { title: "Vielen Dank!", subtitle: "Ihre Bewertung ist abgeschlossen.", message: "Ihre Antworten wurden erfolgreich übermittelt. Unser Team wird die Ergebnisse prüfen und Ihr Personalverantwortlicher wird sich so bald wie möglich mit Ihnen in Verbindung setzen.", note: "Die Ergebnisse sind vertraulich und werden nicht direkt mit Ihnen geteilt." },
};

const LANG_BREAK_UI = {
  en: { title: "Section Complete", message: "You have completed the assessment. Now moving on to", nextBtn: "Begin", ready: "Take a moment to prepare, then click the button when you are ready to start." },
  fr: { title: "Section terminée", message: "Vous avez terminé l'évaluation. Vous passez maintenant à", nextBtn: "Commencer", ready: "Prenez un moment pour vous préparer, puis cliquez sur le bouton lorsque vous êtes prêt(e)." },
  de: { title: "Abschnitt abgeschlossen", message: "Sie haben die Bewertung abgeschlossen. Weiter geht es mit", nextBtn: "Beginnen", ready: "Nehmen Sie sich einen Moment Zeit und klicken Sie dann auf die Schaltfläche, wenn Sie bereit sind." },
};

const CONTENT = {
  en: {
    writingPrompts: [
      "Describe your ideal working environment and explain how it helps you perform at your best.",
      "Tell us about a challenge you faced at work and how you resolved it.",
      "Explain what good teamwork means to you and give an example from your experience.",
    ],
    speakingPrompts: [
      { text: "Please introduce yourself. Tell us your name, where you're from, and your professional background.", duration: 60 },
      { text: "Describe a skill you have that makes you a strong candidate for this role. Be specific.", duration: 60 },
      { text: "What do you enjoy most about working with other people?", duration: 60 },
    ],
    listeningPassage: {
      text: "Our company values three things above all: innovation, collaboration, and integrity. We believe that the best ideas come from diverse teams working together openly. Every employee is encouraged to share their perspective, regardless of their position. We hold regular brainstorming sessions where all ideas are welcomed and explored. Our culture is built on trust, respect, and a shared commitment to excellence.",
      summaryQuestion: "In your own words, summarise what you just heard. Include the main values mentioned, who is encouraged to participate, and how the company fosters collaboration.",
    },
    ui: {
      writingTitle: "Writing Task", writingSub: "Section 1 of 3", writingLabel: "Your prompt",
      writingPlaceholder: "Write your response here. Copy and paste is disabled.",
      writingWords: (n) => `${n} words`,
      timesUp: "Time's up!", timesUpMsg: "Your time has expired. Click next to continue.",
      speakingTitle: (i) => `Speaking Task ${i + 1} of 3`, speakingSub: "Section 2 of 3",
      speakingLabel: "Your question", readingTimer: "Read the question carefully",
      recordingTimer: "Recording in progress", autoRecording: "Recording started automatically",
      stopRec: "⏹ Stop Recording",
      nextPrompt: "Next prompt →", nextListening: "Next: Listening →",
      listeningTitle: "Listening Comprehension", listeningSub: "Section 3 of 3 · Final section",
      listeningIntro: "Listen carefully to the audio passage — you can only hear it once. Then you will have 5 minutes to write a summary of what you heard.",
      listeningDuration: "Audio passage", playBtn: "▶ Play Audio Passage",
      listeningProgress: "Listening… please pay close attention",
      listeningWait: "Your summary question will appear when the audio ends",
      listeningDone: "✅ Audio complete. Write your summary below:",
      summaryPlaceholder: "Write your summary here. Copy and paste is disabled.",
      submitBtn: "Submit & Finish →",
      micError: "Microphone access is required. Please allow microphone permissions and try again.",
      micPermError: "Mic error — check permissions in your browser settings.",
      writingNext: "Next: Speaking →",
      recordingComplete: "Recording complete. Click next when ready.",
    },
  },
  fr: {
    writingPrompts: [
      "Décrivez votre environnement de travail idéal et expliquez comment il vous aide à donner le meilleur de vous-même.",
      "Parlez-nous d'un défi que vous avez rencontré au travail et de la façon dont vous l'avez résolu.",
      "Expliquez ce que signifie pour vous un bon travail d'équipe et donnez un exemple tiré de votre expérience.",
    ],
    speakingPrompts: [
      { text: "Veuillez vous présenter. Dites-nous votre nom, d'où vous venez et votre parcours professionnel.", duration: 60 },
      { text: "Décrivez une compétence que vous possédez et qui fait de vous un candidat solide pour ce poste. Soyez précis.", duration: 60 },
      { text: "Qu'est-ce que vous appréciez le plus dans le travail avec d'autres personnes ?", duration: 60 },
    ],
    listeningPassage: {
      text: "Notre entreprise valorise trois choses par-dessus tout : l'innovation, la collaboration et l'intégrité. Nous croyons que les meilleures idées viennent d'équipes diverses travaillant ensemble ouvertement. Chaque employé est encouragé à partager son point de vue, quelle que soit sa position. Nous organisons régulièrement des séances de brainstorming où toutes les idées sont les bienvenues. Notre culture est fondée sur la confiance, le respect et un engagement commun envers l'excellence.",
      summaryQuestion: "Dans vos propres mots, résumez ce que vous venez d'entendre. Incluez les valeurs principales mentionnées, qui est encouragé à participer et comment l'entreprise favorise la collaboration.",
    },
    ui: {
      writingTitle: "Tâche d'expression écrite", writingSub: "Section 1 sur 3", writingLabel: "Votre sujet",
      writingPlaceholder: "Rédigez votre réponse ici. Le copier-coller est désactivé.",
      writingWords: (n) => `${n} mots`,
      timesUp: "Temps écoulé !", timesUpMsg: "Votre temps est écoulé. Cliquez sur suivant pour continuer.",
      speakingTitle: (i) => `Expression orale ${i + 1} sur 3`, speakingSub: "Section 2 sur 3",
      speakingLabel: "Votre question", readingTimer: "Lisez attentivement la question",
      recordingTimer: "Enregistrement en cours", autoRecording: "Enregistrement démarré automatiquement",
      stopRec: "⏹ Arrêter l'enregistrement",
      nextPrompt: "Sujet suivant →", nextListening: "Suivant : Compréhension orale →",
      listeningTitle: "Compréhension orale", listeningSub: "Section 3 sur 3 · Dernière section",
      listeningIntro: "Écoutez attentivement le passage audio — vous ne pouvez l'entendre qu'une seule fois. Vous aurez ensuite 5 minutes pour rédiger un résumé.",
      listeningDuration: "Passage audio", playBtn: "▶ Écouter le passage audio",
      listeningProgress: "Écoute en cours… concentrez-vous attentivement",
      listeningWait: "Votre question de résumé apparaîtra à la fin de l'audio",
      listeningDone: "✅ Audio terminé. Rédigez votre résumé ci-dessous :",
      summaryPlaceholder: "Rédigez votre résumé ici. Le copier-coller est désactivé.",
      submitBtn: "Soumettre et terminer →",
      micError: "L'accès au microphone est requis. Veuillez autoriser les permissions du microphone et réessayer.",
      micPermError: "Erreur de microphone — vérifiez les autorisations dans les paramètres de votre navigateur.",
      writingNext: "Suivant : Expression orale →",
      recordingComplete: "Enregistrement terminé. Cliquez sur suivant quand vous êtes prêt.",
    },
  },
  de: {
    writingPrompts: [
      "Beschreiben Sie Ihre ideale Arbeitsumgebung und erklären Sie, wie sie Ihnen hilft, Ihr Bestes zu geben.",
      "Erzählen Sie uns von einer Herausforderung, der Sie bei der Arbeit begegnet sind, und wie Sie diese gelöst haben.",
      "Erklären Sie, was gute Teamarbeit für Sie bedeutet, und geben Sie ein Beispiel aus Ihrer Erfahrung.",
    ],
    speakingPrompts: [
      { text: "Bitte stellen Sie sich vor. Nennen Sie uns Ihren Namen, Ihre Herkunft und Ihren beruflichen Werdegang.", duration: 60 },
      { text: "Beschreiben Sie eine Fähigkeit, die Sie zu einem starken Kandidaten für diese Stelle macht. Seien Sie konkret.", duration: 60 },
      { text: "Was genießen Sie am meisten an der Arbeit mit anderen Menschen?", duration: 60 },
    ],
    listeningPassage: {
      text: "Unser Unternehmen legt vor allem Wert auf drei Dinge: Innovation, Zusammenarbeit und Integrität. Wir glauben, dass die besten Ideen aus vielfältigen Teams entstehen, die offen zusammenarbeiten. Jeder Mitarbeiter wird ermutigt, seine Perspektive zu teilen, unabhängig von seiner Position. Wir veranstalten regelmäßig Brainstorming-Sitzungen, bei denen alle Ideen willkommen sind. Unsere Kultur basiert auf Vertrauen, Respekt und einem gemeinsamen Streben nach Exzellenz.",
      summaryQuestion: "Fassen Sie in Ihren eigenen Worten zusammen, was Sie gerade gehört haben. Nennen Sie die wichtigsten Werte, wer zur Teilnahme ermutigt wird und wie das Unternehmen die Zusammenarbeit fördert.",
    },
    ui: {
      writingTitle: "Schreibaufgabe", writingSub: "Abschnitt 1 von 3", writingLabel: "Ihre Aufgabe",
      writingPlaceholder: "Schreiben Sie Ihre Antwort hier. Kopieren und Einfügen ist deaktiviert.",
      writingWords: (n) => `${n} Wörter`,
      timesUp: "Zeit abgelaufen!", timesUpMsg: "Ihre Zeit ist abgelaufen. Klicken Sie auf Weiter, um fortzufahren.",
      speakingTitle: (i) => `Sprechaufgabe ${i + 1} von 3`, speakingSub: "Abschnitt 2 von 3",
      speakingLabel: "Ihre Frage", readingTimer: "Lesen Sie die Frage sorgfältig",
      recordingTimer: "Aufnahme läuft", autoRecording: "Aufnahme automatisch gestartet",
      stopRec: "⏹ Aufnahme stoppen",
      nextPrompt: "Nächste Aufgabe →", nextListening: "Weiter: Hörverstehen →",
      listeningTitle: "Hörverstehen", listeningSub: "Abschnitt 3 von 3 · Letzter Abschnitt",
      listeningIntro: "Hören Sie sich den Audiotext sorgfältig an — Sie können ihn nur einmal hören. Anschließend haben Sie 5 Minuten, um eine Zusammenfassung zu schreiben.",
      listeningDuration: "Audiopassage", playBtn: "▶ Audiotext abspielen",
      listeningProgress: "Zuhören… bitte genau aufpassen",
      listeningWait: "Ihre Zusammenfassungsfrage erscheint nach dem Audio",
      listeningDone: "✅ Audio abgeschlossen. Schreiben Sie Ihre Zusammenfassung unten:",
      summaryPlaceholder: "Schreiben Sie Ihre Zusammenfassung hier. Kopieren und Einfügen ist deaktiviert.",
      submitBtn: "Absenden und beenden →",
      micError: "Mikrofonzugriff ist erforderlich. Bitte erlauben Sie den Mikrofonzugriff und versuchen Sie es erneut.",
      micPermError: "Mikrofonfehler — überprüfen Sie die Berechtigungen in Ihren Browsereinstellungen.",
      writingNext: "Weiter: Sprechen →",
      recordingComplete: "Aufnahme abgeschlossen. Klicken Sie auf Weiter, wenn Sie bereit sind.",
    },
  },
};

const REPORT_LANG_LABELS = { en: "English", fr: "Français", de: "Deutsch" };

const T = {
  bg: "#0a0a0f", surface: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.08)",
  green: "#00D4AA", purple: "#7B61FF", yellow: "#FFD060", red: "#ff6b6b",
  blue: "#38bdf8", text: "#fff", muted: "#888", dim: "#555",
};

// ── AZURE SPEECH HELPER ───────────────────────────────────────────────────────
let cachedSpeechToken = null;
let tokenExpiry = 0;

async function getSpeechToken() {
  if (cachedSpeechToken && Date.now() < tokenExpiry) return cachedSpeechToken;
  const res = await fetch(SPEECH_TOKEN_URL);
  const data = await res.json();
  cachedSpeechToken = data;
  tokenExpiry = Date.now() + 8 * 60 * 1000;
  return data;
}

async function startAzureRecognition(azureLang, onResult, onError) {
  try {
    const { token, region } = await getSpeechToken();
    const sdk = window.SpeechSDK;
    if (!sdk) { onError("Azure Speech SDK not loaded"); return null; }
    const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(token, region);
    speechConfig.speechRecognitionLanguage = azureLang;
    const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
    const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);
    recognizer.recognized = (s, e) => {
      if (e.result.reason === sdk.ResultReason.RecognizedSpeech && e.result.text) {
        onResult(e.result.text);
      }
    };
    recognizer.canceled = (s, e) => {
      if (e.reason === sdk.CancellationReason.Error) onError(e.errorDetails);
    };
    recognizer.startContinuousRecognitionAsync(() => {}, (err) => { onError(err); });
    return recognizer;
  } catch (err) {
    onError(err.message);
    return null;
  }
}

async function stopAzureRecognition(recognizer) {
  if (!recognizer) return;
  return new Promise(resolve => {
    recognizer.stopContinuousRecognitionAsync(
      () => { recognizer.close(); resolve(); },
      () => { try { recognizer.close(); } catch (e) { } resolve(); }
    );
  });
}

function blockCheat(e) {
  if (e.type === "paste" || e.type === "drop" || e.type === "contextmenu") { e.preventDefault(); return false; }
  if (e.type === "keydown") { const isCtrlV = (e.ctrlKey || e.metaKey) && e.key === "v"; if (isCtrlV) { e.preventDefault(); return false; } }
}
function antiCheatProps() {
  return { onPaste: blockCheat, onDrop: blockCheat, onContextMenu: blockCheat, onKeyDown: blockCheat };
}

function CountdownTimer({ seconds, onExpire, color = T.green, label }) {
  const [remaining, setRemaining] = useState(seconds);
  const [expired, setExpired] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => { setRemaining(seconds); setExpired(false); }, [seconds]);
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) { clearInterval(timerRef.current); setExpired(true); onExpire(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [seconds, onExpire]);
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const pct = (remaining / seconds) * 100;
  const timerColor = remaining <= 30 ? T.red : remaining <= 60 ? T.yellow : color;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: T.muted, letterSpacing: 0.5, textTransform: "uppercase" }}>{expired ? "⏰ Time's up" : label || "⏱ Time remaining"}</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 18, color: timerColor }}>{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}</div>
      </div>
      <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 99, height: 6, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: timerColor, borderRadius: 99, transition: "width 1s linear, background 0.3s" }} />
      </div>
    </div>
  );
}

function ProgressBar({ value, max = 100, color = T.green }) {
  return (
    <div style={{ background: "#1a1a2e", borderRadius: 99, height: 8, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(100, (value / max) * 100)}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.3s ease" }} />
    </div>
  );
}

function Btn({ onClick, disabled, children, variant = "primary", style: ex = {} }) {
  const bg = disabled ? "rgba(255,255,255,0.06)" : variant === "primary" ? `linear-gradient(135deg, ${T.green}, #00b894)` : variant === "danger" ? `linear-gradient(135deg, ${T.red}, #ee5a24)` : variant === "purple" ? `linear-gradient(135deg, ${T.purple}, #5a46cc)` : "rgba(255,255,255,0.09)";
  const col = disabled ? "#444" : variant === "ghost" ? T.text : T.bg;
  return <button onClick={onClick} disabled={disabled} style={{ padding: "13px 24px", borderRadius: 12, border: "none", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: disabled ? "not-allowed" : "pointer", background: bg, color: col, transition: "opacity 0.2s", ...ex }}>{children}</button>;
}

function Card({ children, style: ex = {} }) {
  return <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20, padding: 32, ...ex }}>{children}</div>;
}

function SectionHead({ icon, title, sub, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{icon}</div>
      <div><div style={{ fontWeight: 700, fontSize: 16, color: T.text }}>{title}</div><div style={{ color: T.muted, fontSize: 13 }}>{sub}</div></div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, locked = false }) {
  return (
    <div>
      <label style={{ display: "block", color: T.muted, fontSize: 11, marginBottom: 6, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</label>
      <input value={value} onChange={locked ? () => {} : onChange} placeholder={placeholder} readOnly={locked}
        style={{ width: "100%", padding: "13px 15px", background: locked ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: locked ? T.muted : T.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", cursor: locked ? "not-allowed" : "text" }}
        onFocus={e => { if (!locked) e.target.style.borderColor = T.green; }}
        onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.12)"; }}
      />
    </div>
  );
}

function Logo() {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
      <img src={LOGO_URL} alt="Axepta" style={{ height: 40, width: "auto" }} />
      <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.15)" }} />
      <div><div style={{ fontSize: 18, fontWeight: 700, color: T.text, letterSpacing: -0.3 }}>LinguaCheck</div><div style={{ fontSize: 11, color: T.muted, letterSpacing: 0.5 }}>by Axepta</div></div>
    </div>
  );
}

function LangBadge({ lang }) {
  const m = LANG_META[lang];
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 99, background: "rgba(123,97,255,0.12)", border: "1px solid rgba(123,97,255,0.3)", color: T.purple, fontSize: 12, fontWeight: 600 }}>{m.flag} {m.label}</span>;
}

function SessionProgress({ languages, langIdx, section }) {
  const sections = ["writing", "speaking", "listening"];
  return (
    <div style={{ marginBottom: 24 }}>
      {languages.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, justifyContent: "center" }}>
          {languages.map((l, i) => (
            <div key={l} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 99, background: i === langIdx ? "rgba(123,97,255,0.15)" : "rgba(255,255,255,0.04)", border: `1px solid ${i === langIdx ? T.purple : T.border}`, fontSize: 12, color: i < langIdx ? T.green : i === langIdx ? T.purple : T.muted, fontWeight: i === langIdx ? 700 : 400 }}>
              {i < langIdx ? "✓" : ""} {LANG_META[l].flag} {LANG_META[l].label}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 4 }}>
        {sections.map((s, i) => <div key={s} style={{ flex: 1, height: 4, borderRadius: 99, background: i < sections.indexOf(section) ? T.green : i === sections.indexOf(section) ? T.purple : "rgba(255,255,255,0.08)" }} />)}
      </div>
    </div>
  );
}

function LoadingScreen({ message = "Loading..." }) {
  return <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Sora', sans-serif" }}><Fonts /><div style={{ textAlign: "center" }}><Logo /><div style={{ color: T.muted, fontSize: 14, marginTop: 16 }}>{message}</div></div></div>;
}

function ErrorScreen({ message }) {
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ color: T.text, margin: "0 0 10px", fontSize: 20 }}>Link unavailable</h2>
        <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.7 }}>{message}</p>
        <p style={{ color: T.muted, fontSize: 13, marginTop: 16 }}>If you believe this is an error, please contact the recruiter who sent you this link.</p>
      </div>
    </div>
  );
}

function ThankYouScreen({ params }) {
  const uiLang = params?.languages?.[0] || "en";
  const ui = THANKYOU_UI[uiLang] || THANKYOU_UI.en;
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ width: "100%", maxWidth: 520, textAlign: "center" }}>
        <div style={{ marginBottom: 24 }}><Logo /></div>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
        <h1 style={{ color: T.text, margin: "0 0 8px", fontSize: 28, fontWeight: 800 }}>{ui.title}</h1>
        <p style={{ color: T.green, fontSize: 16, fontWeight: 600, margin: "0 0 24px" }}>{ui.subtitle}</p>
        <Card style={{ textAlign: "left", marginBottom: 16 }}><p style={{ color: "#ccc", fontSize: 15, lineHeight: 1.8, margin: 0 }}>{ui.message}</p></Card>
        <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 20px" }}><p style={{ color: T.muted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>🔒 {ui.note}</p></div>
        <div style={{ marginTop: 24, color: T.dim, fontSize: 12 }}>LinguaCheck · GDPR Compliant · Powered by AI</div>
      </div>
    </div>
  );
}

function LangBreakScreen({ nextLang, uiLang, onProceed }) {
  const ui = LANG_BREAK_UI[uiLang] || LANG_BREAK_UI.en;
  const meta = LANG_META[nextLang];
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ width: "100%", maxWidth: 480, textAlign: "center" }}>
        <div style={{ marginBottom: 28 }}><Logo /></div>
        <Card>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <h2 style={{ color: T.green, margin: "0 0 12px", fontSize: 22, fontWeight: 800 }}>{ui.title}</h2>
          <p style={{ color: T.muted, fontSize: 15, margin: "0 0 24px", lineHeight: 1.7 }}>{ui.message}:</p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, background: "rgba(123,97,255,0.12)", border: `1px solid rgba(123,97,255,0.3)`, borderRadius: 14, padding: "16px 28px", marginBottom: 24 }}>
            <span style={{ fontSize: 36 }}>{meta.flag}</span>
            <span style={{ color: T.purple, fontSize: 22, fontWeight: 700 }}>{meta.label}</span>
          </div>
          <p style={{ color: T.muted, fontSize: 13, margin: "0 0 28px", lineHeight: 1.6 }}>{ui.ready}</p>
          <Btn onClick={onProceed} style={{ width: "100%" }}>{ui.nextBtn} {meta.label} →</Btn>
        </Card>
      </div>
    </div>
  );
}

function ConsentScreen({ params, onConsented }) {
  const uiLang = params?.languages?.[0] || "en";
  const ui = CONSENT_UI[uiLang] || CONSENT_UI.en;
  const consentText = CONSENT_TEXT[uiLang] || CONSENT_TEXT.en;
  const [name, setName] = useState(params?.name || "");
  const [email, setEmail] = useState(params?.email || "");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const canContinue = name.trim() && email.trim() && agreed && !submitting;

  const handleContinue = async () => {
    setSubmitting(true); setError("");
    try {
      const res = await fetch(CONSENT_FUNCTION_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ candidateName: name.trim(), candidateEmail: email.trim(), testId: params?.testId || "unknown", consentText, consentVersion: CONSENT_VERSION }) });
      if (!res.ok) throw new Error("Failed");
      if (params?.shortId && MARK_USED_URL) {
        fetch(MARK_USED_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ shortId: params.shortId }) }).catch(e => console.error("Mark used failed:", e));
      }
      onConsented({ ...params, name: name.trim(), email: email.trim() });
    } catch (e) { setError(ui.errorMsg); setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ width: "100%", maxWidth: 540 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}><Logo /></div>
        <Card>
          <h2 style={{ color: T.text, margin: "0 0 6px", fontSize: 19, fontWeight: 700 }}>{ui.title}</h2>
          <p style={{ color: T.muted, margin: "0 0 24px", fontSize: 13, lineHeight: 1.6 }}>{ui.subtitle}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 13, marginBottom: 24 }}>
            <Field label={ui.nameLabel} value={name} onChange={e => setName(e.target.value)} placeholder={ui.namePlaceholder} />
            <Field label={ui.emailLabel} value={email} onChange={e => setEmail(e.target.value)} placeholder={ui.emailPlaceholder} />
          </div>
          <div style={{ background: "rgba(0,212,170,0.04)", border: "1px solid rgba(0,212,170,0.18)", borderRadius: 14, padding: "18px 20px", marginBottom: 20 }}>
            <div style={{ color: T.green, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>{ui.privacyTitle}</div>
            <p style={{ color: "#ccc", fontSize: 13, lineHeight: 1.8, margin: 0 }}>{consentText}</p>
          </div>
          <div onClick={() => setAgreed(!agreed)} style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", marginBottom: 24, padding: "14px 16px", borderRadius: 12, background: agreed ? "rgba(0,212,170,0.06)" : "rgba(255,255,255,0.02)", border: `1px solid ${agreed ? "rgba(0,212,170,0.3)" : T.border}`, transition: "all 0.2s" }}>
            <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${agreed ? T.green : T.muted}`, background: agreed ? T.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all 0.2s" }}>
              {agreed && <span style={{ color: T.bg, fontSize: 12, fontWeight: 900 }}>✓</span>}
            </div>
            <span style={{ color: agreed ? T.text : T.muted, fontSize: 13, lineHeight: 1.6, transition: "color 0.2s" }}>{ui.checkboxText}</span>
          </div>
          {error && <div style={{ color: T.red, fontSize: 13, background: "rgba(255,107,107,0.08)", padding: "10px 14px", borderRadius: 8, marginBottom: 16 }}>{error}</div>}
          <Btn onClick={handleContinue} disabled={!canContinue} style={{ width: "100%" }}>{submitting ? ui.submittingBtn : ui.continueBtn}</Btn>
          <div style={{ marginTop: 16, textAlign: "center", color: T.dim, fontSize: 11, lineHeight: 1.6 }}>{ui.footer}<br />{ui.withdraw} <span style={{ color: T.muted }}>dpo@axepta.com</span></div>
        </Card>
      </div>
    </div>
  );
}

function DeviceCheck({ uiLang, onPassed }) {
  const [browser, setBrowser] = useState("chrome");
  const [speakerStatus, setSpeakerStatus] = useState("idle");
  const [micStatus, setMicStatus] = useState("idle");
  const [micLevel, setMicLevel] = useState(0);
  const [showSpeakerFix, setShowSpeakerFix] = useState(false);
  const [showMicFix, setShowMicFix] = useState(false);
  const micIntervalRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    setBrowser(ua.includes("edg/") ? "edge" : "chrome");
    return () => { clearInterval(micIntervalRef.current); if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, []);

  const testSpeaker = () => {
    setSpeakerStatus("testing"); setShowSpeakerFix(false);
    try {
      const audio = new Audio("https://linguacheckstorage.blob.core.windows.net/linguacheckstorage/mixkit-race-countdown-1953.wav");
      audio.volume = 0.6;
      audio.play().then(() => { audio.onended = () => setSpeakerStatus("pass"); }).catch(() => { setSpeakerStatus("fail"); setShowSpeakerFix(true); });
    } catch (e) { setSpeakerStatus("fail"); setShowSpeakerFix(true); }
  };

  const testMic = async () => {
    setMicStatus("testing"); setShowMicFix(false); setMicLevel(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256; source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let maxLevel = 0; let ticks = 0;
      micIntervalRef.current = setInterval(() => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const level = Math.min(100, avg * 2);
        setMicLevel(level);
        if (level > maxLevel) maxLevel = level;
        ticks++;
        if (ticks >= 30) {
          clearInterval(micIntervalRef.current);
          stream.getTracks().forEach(t => t.stop());
          ctx.close();
          streamRef.current = null;
          if (maxLevel > 5) { setMicStatus("pass"); } else { setMicStatus("fail"); setShowMicFix(true); }
          setMicLevel(0);
        }
      }, 100);
    } catch (e) { setMicStatus("fail"); setShowMicFix(true); }
  };

  const canProceed = speakerStatus === "pass" && micStatus === "pass";
  const statusIcon = (s) => s === "idle" ? "⚪" : s === "testing" ? "⏳" : s === "pass" ? "✅" : "❌";

  const browserInstructions = {
    chrome: {
      name: "Google Chrome",
      micSetup: ["Click the 🔒 padlock icon in the address bar", "Click \"Site settings\"", "Find \"Microphone\" and change it to \"Allow\"", "Refresh this page and click Allow when Chrome asks for permission", "If still blocked: Chrome menu (⋮) → Settings → Privacy and security → Site settings → Microphone"],
      speakerSetup: ["Click the 🔒 padlock icon in the address bar", "Click \"Site settings\"", "Find \"Sound\" and change it to \"Allow\"", "Check your system volume is not muted", "Chrome menu (⋮) → Settings → Privacy and security → Site settings → Additional permissions → Sound"],
      micPermFix: ["Click the 🔒 padlock → Site settings → Microphone → Allow", "Chrome menu (⋮) → Settings → Privacy and security → Site settings → Microphone", "Ensure linguacheck.axepta.com is in the \"Allowed\" list", "Windows Settings → Privacy → Microphone → Allow apps to access your microphone", "Refresh the page after making changes"],
      speakerPermFix: ["Check your computer volume — press the volume up key", "Click the 🔒 padlock → Site settings → Sound → Allow", "Check Chrome is not muted: right-click the Chrome tab", "Chrome menu (⋮) → Settings → Privacy and security → Site settings → Additional permissions → Sound", "Refresh the page after making any changes"],
    },
    edge: {
      name: "Microsoft Edge",
      micSetup: ["Click the 🔒 padlock icon in the address bar", "Click \"Permissions for this site\"", "Set \"Microphone\" to \"Allow\"", "Refresh this page and click Allow when Edge asks for permission", "If still blocked: Edge menu (...) → Settings → Cookies and site permissions → Microphone"],
      speakerSetup: ["Click the 🔒 padlock icon in the address bar", "Click \"Permissions for this site\"", "Check your system volume is not muted", "Edge menu (...) → Settings → Cookies and site permissions → Media autoplay → Allow", "Refresh the page after making changes"],
      micPermFix: ["Click the 🔒 padlock → Permissions for this site → Microphone → Allow", "Edge menu (...) → Settings → Cookies and site permissions → Microphone", "Ensure linguacheck.axepta.com is in the \"Allow\" list", "Windows Settings → Privacy → Microphone → ensure Microsoft Edge is allowed", "Refresh the page after making changes"],
      speakerPermFix: ["Check your computer volume — press the volume up key", "Right-click the speaker icon in the Windows taskbar → Open Volume Mixer → check Edge is not muted", "Click the 🔒 padlock → Permissions for this site → check Sound settings", "Edge menu (...) → Settings → Cookies and site permissions → Media autoplay → Allow", "Refresh the page after making any changes"],
    },
  };

  const instr = browserInstructions[browser];

  const labels = {
    en: { title: "Device Check", subtitle: "Before starting your assessment, let's make sure your speaker and microphone are working correctly.", browserDetected: "Browser detected", speakerTest: "Speaker Test", speakerDesc: "Click to play a short audio clip. You should hear it clearly.", testSpeaker: "▶ Test Speaker", speakerTesting: "Playing...", speakerPass: "✅ Speaker working", speakerFail: "❌ Speaker not detected", micTest: "Microphone Test", micDesc: "Click to test your microphone. Speak clearly for 3 seconds.", testMic: "🎙 Test Microphone", micTesting: "Listening... speak now!", micPass: "✅ Microphone working", micFail: "❌ Microphone not detected", howToFix: "How to fix this:", setupTitle: "How to enable", proceed: "Continue to Consent →", proceedDisabled: "Complete both tests above to proceed", bothPassed: "All checks passed! Click below to continue.", micLevelLabel: "Mic level", retestSpeaker: "Re-test Speaker", retestMic: "Re-test Microphone" },
    fr: { title: "Vérification de l'appareil", subtitle: "Avant de commencer votre évaluation, vérifions que votre haut-parleur et votre microphone fonctionnent correctement.", browserDetected: "Navigateur détecté", speakerTest: "Test du haut-parleur", speakerDesc: "Cliquez pour écouter un court extrait audio.", testSpeaker: "▶ Tester le haut-parleur", speakerTesting: "Lecture...", speakerPass: "✅ Haut-parleur fonctionnel", speakerFail: "❌ Haut-parleur non détecté", micTest: "Test du microphone", micDesc: "Cliquez pour tester votre microphone. Parlez clairement pendant 3 secondes.", testMic: "🎙 Tester le microphone", micTesting: "Écoute... parlez maintenant !", micPass: "✅ Microphone fonctionnel", micFail: "❌ Microphone non détecté", howToFix: "Comment résoudre ce problème :", setupTitle: "Comment activer", proceed: "Continuer vers le consentement →", proceedDisabled: "Complétez les deux tests ci-dessus pour continuer", bothPassed: "Tous les tests réussis ! Cliquez ci-dessous pour continuer.", micLevelLabel: "Niveau micro", retestSpeaker: "Re-tester le haut-parleur", retestMic: "Re-tester le microphone" },
    de: { title: "Geräteprüfung", subtitle: "Bevor Sie mit der Bewertung beginnen, stellen wir sicher, dass Ihr Lautsprecher und Mikrofon ordnungsgemäß funktionieren.", browserDetected: "Erkannter Browser", speakerTest: "Lautsprechertest", speakerDesc: "Klicken Sie, um einen kurzen Audioclip abzuspielen.", testSpeaker: "▶ Lautsprecher testen", speakerTesting: "Wird abgespielt...", speakerPass: "✅ Lautsprecher funktioniert", speakerFail: "❌ Lautsprecher nicht erkannt", micTest: "Mikrofontest", micDesc: "Klicken Sie, um Ihr Mikrofon zu testen. Sprechen Sie 3 Sekunden lang deutlich.", testMic: "🎙 Mikrofon testen", micTesting: "Zuhören... sprechen Sie jetzt!", micPass: "✅ Mikrofon funktioniert", micFail: "❌ Mikrofon nicht erkannt", howToFix: "So beheben Sie das Problem:", setupTitle: "So aktivieren Sie", proceed: "Weiter zur Einwilligung →", proceedDisabled: "Schließen Sie beide Tests oben ab, um fortzufahren", bothPassed: "Alle Prüfungen bestanden! Klicken Sie unten, um fortzufahren.", micLevelLabel: "Mikrofonpegel", retestSpeaker: "Lautsprecher erneut testen", retestMic: "Mikrofon erneut testen" },
  };

  const L = labels[uiLang] || labels.en;

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ width: "100%", maxWidth: 600 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}><Logo /></div>
        <Card>
          <h2 style={{ color: T.text, margin: "0 0 8px", fontSize: 20, fontWeight: 700 }}>{L.title}</h2>
          <p style={{ color: T.muted, margin: "0 0 24px", fontSize: 14, lineHeight: 1.6 }}>{L.subtitle}</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
            <div style={{ color: T.muted, fontSize: 12, alignSelf: "center", marginRight: 4 }}>{L.browserDetected}:</div>
            {["chrome", "edge"].map(b => (
              <button key={b} onClick={() => setBrowser(b)} style={{ padding: "8px 16px", borderRadius: 10, border: `2px solid ${browser === b ? T.purple : T.border}`, background: browser === b ? "rgba(123,97,255,0.12)" : "transparent", color: browser === b ? T.purple : T.muted, fontFamily: "inherit", cursor: "pointer", fontWeight: browser === b ? 700 : 400, fontSize: 13, transition: "all 0.2s" }}>
                {b === "chrome" ? "🟡 Chrome" : "🔵 Edge"}
              </button>
            ))}
          </div>
          <div style={{ background: T.surface, border: `1px solid ${speakerStatus === "pass" ? T.green + "44" : speakerStatus === "fail" ? T.red + "44" : T.border}`, borderRadius: 14, padding: "18px 20px", marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div><div style={{ color: T.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>🔊 {L.speakerTest}</div><div style={{ color: T.muted, fontSize: 13 }}>{L.speakerDesc}</div></div>
              <div style={{ fontSize: 22 }}>{statusIcon(speakerStatus)}</div>
            </div>
            {speakerStatus === "idle" && <Btn onClick={testSpeaker} variant="ghost" style={{ marginTop: 10 }}>{L.testSpeaker}</Btn>}
            {speakerStatus === "testing" && <div style={{ color: T.yellow, fontSize: 13, marginTop: 10 }}>{L.speakerTesting}</div>}
            {speakerStatus === "pass" && <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}><div style={{ color: T.green, fontSize: 13, fontWeight: 600 }}>{L.speakerPass}</div><button onClick={() => { setSpeakerStatus("idle"); setShowSpeakerFix(false); }} style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 11, padding: "4px 10px" }}>{L.retestSpeaker}</button></div>}
            {speakerStatus === "fail" && <div style={{ marginTop: 10 }}>
              <div style={{ color: T.red, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{L.speakerFail}</div>
              <button onClick={() => setShowSpeakerFix(!showSpeakerFix)} style={{ background: "transparent", border: `1px solid ${T.red}44`, borderRadius: 8, color: T.red, cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: "6px 12px", marginBottom: showSpeakerFix ? 12 : 0 }}>{showSpeakerFix ? "▲ Hide" : "▼ Show"} {L.howToFix}</button>
              {showSpeakerFix && <div style={{ background: "rgba(255,107,107,0.06)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 10, padding: "12px 16px", marginBottom: 10 }}>{instr.speakerPermFix.map((step, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}><span style={{ color: T.red, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span><span style={{ color: "#ccc", fontSize: 12, lineHeight: 1.5 }}>{step}</span></div>)}</div>}
              <Btn onClick={testSpeaker} variant="ghost" style={{ fontSize: 12, padding: "10px 18px" }}>{L.retestSpeaker}</Btn>
            </div>}
          </div>
          <div style={{ background: T.surface, border: `1px solid ${micStatus === "pass" ? T.green + "44" : micStatus === "fail" ? T.red + "44" : T.border}`, borderRadius: 14, padding: "18px 20px", marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div><div style={{ color: T.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>🎙 {L.micTest}</div><div style={{ color: T.muted, fontSize: 13 }}>{L.micDesc}</div></div>
              <div style={{ fontSize: 22 }}>{statusIcon(micStatus)}</div>
            </div>
            {micStatus === "idle" && <Btn onClick={testMic} variant="ghost" style={{ marginTop: 10 }}>{L.testMic}</Btn>}
            {micStatus === "testing" && <div style={{ marginTop: 12 }}><div style={{ color: T.green, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{L.micTesting}</div><div style={{ fontSize: 11, color: T.muted, marginBottom: 6, letterSpacing: 0.5, textTransform: "uppercase" }}>{L.micLevelLabel}</div><div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 99, height: 12, overflow: "hidden" }}><div style={{ width: `${micLevel}%`, height: "100%", background: micLevel > 10 ? T.green : T.muted, borderRadius: 99, transition: "width 0.1s ease" }} /></div></div>}
            {micStatus === "pass" && <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}><div style={{ color: T.green, fontSize: 13, fontWeight: 600 }}>{L.micPass}</div><button onClick={() => { setMicStatus("idle"); setShowMicFix(false); }} style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 11, padding: "4px 10px" }}>{L.retestMic}</button></div>}
            {micStatus === "fail" && <div style={{ marginTop: 10 }}>
              <div style={{ color: T.red, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{L.micFail}</div>
              <button onClick={() => setShowMicFix(!showMicFix)} style={{ background: "transparent", border: `1px solid ${T.red}44`, borderRadius: 8, color: T.red, cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: "6px 12px", marginBottom: showMicFix ? 12 : 0 }}>{showMicFix ? "▲ Hide" : "▼ Show"} {L.howToFix}</button>
              {showMicFix && <div style={{ background: "rgba(255,107,107,0.06)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 10, padding: "12px 16px", marginBottom: 10 }}>{instr.micPermFix.map((step, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}><span style={{ color: T.red, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span><span style={{ color: "#ccc", fontSize: 12, lineHeight: 1.5 }}>{step}</span></div>)}</div>}
              <Btn onClick={testMic} variant="ghost" style={{ fontSize: 12, padding: "10px 18px" }}>{L.retestMic}</Btn>
            </div>}
          </div>
          <div style={{ background: "rgba(123,97,255,0.06)", border: "1px solid rgba(123,97,255,0.2)", borderRadius: 14, padding: "16px 20px", marginBottom: 24 }}>
            <div style={{ color: T.purple, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12 }}>{L.setupTitle} Microphone & Speaker in {instr.name}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ color: "#ccc", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>🎙 Microphone</div>
                {instr.micSetup.map((step, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}><span style={{ color: T.purple, fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{i + 1}.</span><span style={{ color: T.muted, fontSize: 12, lineHeight: 1.5 }}>{step}</span></div>)}
              </div>
              <div>
                <div style={{ color: "#ccc", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>🔊 Speaker</div>
                {instr.speakerSetup.map((step, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}><span style={{ color: T.green, fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{i + 1}.</span><span style={{ color: T.muted, fontSize: 12, lineHeight: 1.5 }}>{step}</span></div>)}
              </div>
            </div>
          </div>
          {canProceed && <div style={{ background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.25)", borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "center", color: T.green, fontSize: 14, fontWeight: 600 }}>{L.bothPassed}</div>}
          <div style={{ textAlign: "center" }}>
            <Btn onClick={onPassed} disabled={!canProceed} style={{ width: "100%" }}>{L.proceed}</Btn>
            {!canProceed && <div style={{ color: T.dim, fontSize: 12, marginTop: 10 }}>{L.proceedDisabled}</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function getEmailLang(languages) {
  if (languages.includes("en")) return "en";
  if (languages.includes("fr")) return "fr";
  return "de";
}

function buildHtmlDraftEmail(params, shortUrl) {
  const lang = getEmailLang(params.languages || ["en"]);
  const langNames = { en: "English", fr: "Français", de: "Deutsch" };
  const assessedLangs = (params.languages || ["en"]).map(l => langNames[l] || l).join(", ");
  const timeEst = (params.languages || ["en"]).length * 8;
  const companyDisplay = params.companyName || (params.companyWebsite ? params.companyWebsite.replace(/https?:\/\//, "").replace(/\/$/, "") : "");

  const content = {
    en: {
      subject: `Language Assessment Invitation — ${params.role}${companyDisplay ? ` | ${companyDisplay}` : ""}`,
      greeting: `Dear ${params.name},`,
      intro1: `Thank you for your interest in the <strong>${params.role}</strong> position${companyDisplay ? ` at <strong>${companyDisplay}</strong>` : ""}.`,
      intro2: `As part of our interview process, we would like to invite you to complete a short online language assessment.`,
      aboutTitle: "About the Assessment",
      aboutDesc: `The assessment evaluates your language proficiency in <strong>${assessedLangs}</strong> and takes approximately <strong>${timeEst} minutes</strong> to complete. It consists of three sections:`,
      sections: [
        { icon: "✍️", color: "#f59e0b", title: "Writing", desc: `You will be given a written prompt and asked to respond. You have <strong>4 minutes</strong> for the first task and <strong>3 minutes</strong> for subsequent tasks.` },
        { icon: "🎙", color: "#7c3aed", title: "Speaking", desc: `You will respond verbally to three spoken prompts. You have <strong>30 seconds</strong> to read each question, then <strong>60 seconds</strong> to respond.` },
        { icon: "👂", color: "#059669", title: "Listening", desc: `You will listen to a short audio passage and then have <strong>5 minutes</strong> to write a summary of what you heard.` },
      ],
      importantTitle: "Important Information",
      importantItems: [
        `Your results are <strong>confidential</strong> — they will be sent directly to your recruiter and will not be shared with you.`,
        `The assessment link below can <strong>only be used once</strong>. Please ensure you are fully ready before clicking it.`,
        `<strong>Copy and paste is disabled</strong> throughout the assessment to ensure a fair and accurate evaluation.`,
      ],
      tipsTitle: "Tips for Best Results",
      tips: [
        `Find a <strong>quiet room</strong> free from distractions and background noise`,
        `Use <strong>Google Chrome or Microsoft Edge</strong> for the best experience`,
        `<strong>Test your microphone and speakers</strong> before starting — the assessment includes a built-in device check`,
        `Ensure you have a <strong>stable internet connection</strong>`,
        `Set aside <strong>approximately ${timeEst} minutes</strong> of uninterrupted time`,
        `Have your device <strong>fully charged or plugged in</strong>`,
      ],
      linkTitle: "Your Assessment Link",
      linkDesc: `Click the button below to begin your assessment. This link is unique to you and can only be used once.`,
      linkBtn: "Start My Assessment →",
      linkWarning: `⚠ This link is personal and can only be used once. Please do not share it.`,
      closing: `If you experience any technical difficulties or have questions, please reply to this email.`,
      lookFwd: `We look forward to receiving your assessment.`,
      regards: `Kind regards,`,
    },
    fr: {
      subject: `Invitation à l'évaluation linguistique — ${params.role}${companyDisplay ? ` | ${companyDisplay}` : ""}`,
      greeting: `Bonjour ${params.name},`,
      intro1: `Nous vous remercions de l'intérêt que vous portez au poste de <strong>${params.role}</strong>${companyDisplay ? ` chez <strong>${companyDisplay}</strong>` : ""}.`,
      intro2: `Dans le cadre de notre processus de recrutement, nous vous invitons à compléter une évaluation linguistique en ligne.`,
      aboutTitle: "À propos de l'évaluation",
      aboutDesc: `L'évaluation mesure vos compétences linguistiques en <strong>${assessedLangs}</strong> et dure environ <strong>${timeEst} minutes</strong>. Elle comprend trois sections :`,
      sections: [
        { icon: "✍️", color: "#f59e0b", title: "Expression écrite", desc: `Vous disposerez de <strong>4 minutes</strong> pour la première tâche et de <strong>3 minutes</strong> pour les suivantes.` },
        { icon: "🎙", color: "#7c3aed", title: "Expression orale", desc: `Vous aurez <strong>30 secondes</strong> pour lire chaque question, puis <strong>60 secondes</strong> pour répondre.` },
        { icon: "👂", color: "#059669", title: "Compréhension orale", desc: `Vous écouterez un court passage audio, puis aurez <strong>5 minutes</strong> pour rédiger un résumé.` },
      ],
      importantTitle: "Informations importantes",
      importantItems: [
        `Vos résultats sont <strong>confidentiels</strong> — ils seront transmis directement à votre recruteur.`,
        `Le lien ci-dessous ne peut être utilisé <strong>qu'une seule fois</strong>.`,
        `Le <strong>copier-coller est désactivé</strong> pendant toute l'évaluation.`,
      ],
      tipsTitle: "Conseils pour de meilleurs résultats",
      tips: [
        `Choisissez une <strong>pièce calme</strong>, sans distractions`,
        `Utilisez <strong>Google Chrome ou Microsoft Edge</strong>`,
        `<strong>Testez votre microphone et vos haut-parleurs</strong> avant de commencer`,
        `Assurez-vous d'une <strong>connexion internet stable</strong>`,
        `Prévoyez environ <strong>${timeEst} minutes</strong> sans interruption`,
        `Appareil <strong>chargé ou branché</strong>`,
      ],
      linkTitle: "Votre lien d'évaluation",
      linkDesc: `Cliquez sur le bouton ci-dessous pour commencer.`,
      linkBtn: "Commencer mon évaluation →",
      linkWarning: `⚠ Ce lien est personnel et ne peut être utilisé qu'une seule fois.`,
      closing: `En cas de difficultés techniques, répondez à cet e-mail.`,
      lookFwd: `Nous vous souhaitons une excellente évaluation.`,
      regards: `Cordialement,`,
    },
    de: {
      subject: `Einladung zur Sprachbewertung — ${params.role}${companyDisplay ? ` | ${companyDisplay}` : ""}`,
      greeting: `Guten Tag ${params.name},`,
      intro1: `vielen Dank für Ihr Interesse an der Stelle als <strong>${params.role}</strong>${companyDisplay ? ` bei <strong>${companyDisplay}</strong>` : ""}.`,
      intro2: `Im Rahmen unseres Bewerbungsprozesses möchten wir Sie zu einer kurzen Online-Sprachbewertung einladen.`,
      aboutTitle: "Über die Bewertung",
      aboutDesc: `Die Bewertung misst Ihre Sprachkenntnisse in <strong>${assessedLangs}</strong> und dauert ca. <strong>${timeEst} Minuten</strong>. Sie besteht aus drei Abschnitten:`,
      sections: [
        { icon: "✍️", color: "#f59e0b", title: "Schreiben", desc: `Sie haben <strong>4 Minuten</strong> für die erste Aufgabe und <strong>3 Minuten</strong> für die folgenden.` },
        { icon: "🎙", color: "#7c3aed", title: "Sprechen", desc: `Sie haben <strong>30 Sekunden</strong> zum Lesen, dann <strong>60 Sekunden</strong> zum Antworten.` },
        { icon: "👂", color: "#059669", title: "Hören", desc: `Sie hören einen Audiotext und haben <strong>5 Minuten</strong> für eine Zusammenfassung.` },
      ],
      importantTitle: "Wichtige Informationen",
      importantItems: [
        `Ihre Ergebnisse sind <strong>vertraulich</strong> — direkt an Ihren Recruiter.`,
        `Der Link kann <strong>nur einmal verwendet</strong> werden.`,
        `<strong>Kopieren und Einfügen ist deaktiviert</strong>.`,
      ],
      tipsTitle: "Tipps für beste Ergebnisse",
      tips: [
        `<strong>Ruhiger Raum</strong> ohne Ablenkungen`,
        `<strong>Google Chrome oder Microsoft Edge</strong>`,
        `<strong>Mikrofon und Lautsprecher testen</strong> vor dem Start`,
        `<strong>Stabile Internetverbindung</strong>`,
        `Ca. <strong>${timeEst} Minuten</strong> ungestörte Zeit`,
        `Gerät <strong>geladen oder angeschlossen</strong>`,
      ],
      linkTitle: "Ihr Bewertungslink",
      linkDesc: `Klicken Sie auf die Schaltfläche unten, um zu beginnen.`,
      linkBtn: "Bewertung starten →",
      linkWarning: `⚠ Dieser Link kann nur einmal verwendet werden.`,
      closing: `Bei Schwierigkeiten antworten Sie bitte auf diese E-Mail.`,
      lookFwd: `Wir freuen uns auf Ihre Bewertung.`,
      regards: `Mit freundlichen Grüßen,`,
    },
  };

  const c = content[lang];
  const html = `<!DOCTYPE html>
<html lang="${lang}">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>body{margin:0;padding:0;background:#f3f4f6;font-family:Arial,Helvetica,sans-serif;}p{margin:0;padding:0;}</style>
</head>
<body>
<table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#f3f4f6;padding:32px 16px;">
<tr><td align="center">
<table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;">
  <tr><td style="background:#0d0d1a;border-radius:12px 12px 0 0;padding:20px 32px;">
    <table cellpadding="0" cellspacing="0" border="0"><tr>
      <td style="padding-right:16px;vertical-align:middle;"><img src="https://linguacheckstorage.blob.core.windows.net/linguacheckstorage/axepta-logo.png" alt="Axepta" width="24" style="display:block;width:24px;height:auto;" /></td>
      <td style="border-left:1px solid rgba(255,255,255,0.2);padding-left:16px;vertical-align:middle;">
        <div style="color:#ffffff;font-size:15px;font-weight:700;font-family:Arial,sans-serif;">LinguaCheck</div>
        <div style="color:#888888;font-size:11px;font-family:Arial,sans-serif;">by Axepta</div>
      </td>
    </tr></table>
  </td></tr>
  <tr><td style="background:linear-gradient(to right,#00D4AA,#7B61FF);height:3px;font-size:0;line-height:0;">&nbsp;</td></tr>
  <tr><td style="background:#ffffff;padding:36px 32px;">
    <p style="color:#111827;font-size:16px;font-weight:700;font-family:Arial,sans-serif;margin-bottom:20px;">${c.greeting}</p>
    <p style="color:#374151;font-size:14px;line-height:1.8;font-family:Arial,sans-serif;margin-bottom:12px;">${c.intro1}</p>
    <p style="color:#374151;font-size:14px;line-height:1.8;font-family:Arial,sans-serif;margin-bottom:32px;">${c.intro2}</p>
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:10px;margin-bottom:24px;">
    <tr><td style="padding:22px 24px;">
      <p style="color:#111827;font-size:15px;font-weight:700;font-family:Arial,sans-serif;margin-bottom:12px;">📋 ${c.aboutTitle}</p>
      <p style="color:#374151;font-size:14px;line-height:1.8;font-family:Arial,sans-serif;margin-bottom:20px;">${c.aboutDesc}</p>
      ${c.sections.map(s => `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom:18px;"><tr><td style="width:40px;vertical-align:top;padding-top:2px;"><div style="width:32px;height:32px;background:${s.color}22;border-radius:8px;text-align:center;line-height:32px;font-size:16px;">${s.icon}</div></td><td style="vertical-align:top;padding-left:12px;"><p style="color:#111827;font-size:14px;font-weight:700;font-family:Arial,sans-serif;margin-bottom:6px;">${s.title}</p><p style="color:#6b7280;font-size:13px;line-height:1.75;font-family:Arial,sans-serif;">${s.desc}</p></td></tr></table>`).join("")}
    </td></tr></table>
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#fffbeb;border:1px solid #fcd34d;border-radius:10px;margin-bottom:24px;">
    <tr><td style="padding:22px 24px;">
      <p style="color:#92400e;font-size:15px;font-weight:700;font-family:Arial,sans-serif;margin-bottom:16px;">⚠️ ${c.importantTitle}</p>
      ${c.importantItems.map(item => `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom:14px;"><tr><td style="width:20px;vertical-align:top;color:#d97706;font-size:16px;font-weight:700;padding-top:1px;">•</td><td style="vertical-align:top;padding-left:10px;color:#374151;font-size:13px;line-height:1.8;font-family:Arial,sans-serif;">${item}</td></tr></table>`).join("")}
    </td></tr></table>
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;margin-bottom:8px;">
    <tr><td style="padding:22px 24px;">
      <p style="color:#14532d;font-size:15px;font-weight:700;font-family:Arial,sans-serif;margin-bottom:16px;">💡 ${c.tipsTitle}</p>
      ${c.tips.map(tip => `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom:14px;"><tr><td style="width:20px;vertical-align:top;color:#16a34a;font-size:14px;font-weight:700;padding-top:1px;">✓</td><td style="vertical-align:top;padding-left:10px;color:#374151;font-size:13px;line-height:1.8;font-family:Arial,sans-serif;">${tip}</td></tr></table>`).join("")}
    </td></tr></table>
  </td></tr>
  <tr><td style="background:#0d0d1a;padding:32px;">
    <p style="color:#ffffff;font-size:16px;font-weight:700;font-family:Arial,sans-serif;margin-bottom:10px;text-align:center;">🔗 ${c.linkTitle}</p>
    <p style="color:#9ca3af;font-size:13px;line-height:1.7;font-family:Arial,sans-serif;margin-bottom:24px;text-align:center;">${c.linkDesc}</p>
    <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td align="center" style="padding-bottom:20px;"><a href="${shortUrl}" style="display:inline-block;background:#00D4AA;color:#0a0a0f;font-size:15px;font-weight:700;font-family:Arial,sans-serif;padding:14px 36px;border-radius:10px;text-decoration:none;">${c.linkBtn}</a></td></tr></table>
    <p style="color:#6b7280;font-size:12px;font-family:Arial,sans-serif;text-align:center;margin-bottom:0;">${c.linkWarning}</p>
  </td></tr>
  <tr><td style="background:#ffffff;padding:28px 32px;border-radius:0 0 12px 12px;border-top:1px solid #e5e7eb;">
    <p style="color:#374151;font-size:14px;line-height:1.8;font-family:Arial,sans-serif;margin-bottom:10px;">${c.closing}</p>
    <p style="color:#374151;font-size:14px;line-height:1.8;font-family:Arial,sans-serif;margin-bottom:24px;">${c.lookFwd}</p>
    <p style="color:#374151;font-size:14px;font-family:Arial,sans-serif;margin-bottom:6px;">${c.regards}</p>
    <p style="color:#111827;font-size:14px;font-weight:700;font-family:Arial,sans-serif;margin-bottom:4px;">${params.manager}</p>
    <p style="color:#6b7280;font-size:13px;font-family:Arial,sans-serif;margin-bottom:0;">${params.managerEmail}</p>
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top:24px;"><tr><td style="padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;color:#9ca3af;font-size:11px;font-family:Arial,sans-serif;">Powered by LinguaCheck &nbsp;·&nbsp; Axepta &nbsp;·&nbsp; Confidential</td></tr></table>
  </td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
  return { subject: c.subject, html };
}

async function analyzeWithClaude(langResults, reportLang, candidateName, role) {
  const payload = {
    candidateName,
    role,
    reportLang,
    langResults: langResults.map(lr => {
      const c = CONTENT[lr.lang];
      const customLangContent = lr.customContent?.[lr.lang];
      const writingPrompt = customLangContent?.writingPrompts?.[lr.writingIdx] ?? c.writingPrompts[lr.writingIdx];
      const speakingPrompts = customLangContent?.speakingPrompts ?? c.speakingPrompts;
      const listeningPassage = customLangContent?.listeningPassage ?? c.listeningPassage;
      return {
        lang: lr.lang,
        writingPrompt,
        writingResponse: lr.writingResponse,
        speakingData: lr.speakingTranscripts.map((t, i) => ({
          prompt: speakingPrompts[i]?.text || "",
          transcript: t || "",
        })),
        listeningPassage: listeningPassage.text,
        summaryQuestion: listeningPassage.summaryQuestion,
        listeningSummary: lr.listeningSummary,
      };
    }),
  };

  const res = await fetch(ANALYZE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error("Analysis function failed: " + res.status);
  return await res.json();
}

async function sendReport(results, params) {
  await fetch(SEND_REPORT_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ results, params }) });
}

function Setup({ onPreview }) {
  const [f, setF] = useState({ name: "", email: "", role: "", companyName: "", companyWebsite: "", manager: "", managerEmail: "" });
  const [selectedLangs, setSelectedLangs] = useState(["en"]);
  const [reportLang, setReportLang] = useState("en");
  const [copied, setCopied] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatingStep, setGeneratingStep] = useState(0);
  const [generatedCompanyName, setGeneratedCompanyName] = useState("");
  const [recruiter, setRecruiter] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loginError, setLoginError] = useState("");
  const stepIntervalRef = useRef(null);

  const verifyAndSetRecruiter = (account) => {
    const tenantId = account.tenantId || account.idTokenClaims?.tid;
    if (tenantId && tenantId !== AXEPTA_TENANT_ID) return false;
    setRecruiter({ name: account.name, email: account.username });
    setF(p => ({ ...p, manager: account.name, managerEmail: account.username }));
    return true;
  };

  useEffect(() => {
    msalInstance.initialize().then(async () => {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        if (!verifyAndSetRecruiter(accounts[0])) {
          await msalInstance.logoutPopup({ onRedirectNavigate: () => false }).catch(() => {});
          setAccessDenied(true);
        }
      } else {
        try {
          const response = await msalInstance.ssoSilent({ ...loginRequest, prompt: "none" });
          if (!verifyAndSetRecruiter(response.account)) {
            await msalInstance.logoutPopup({ onRedirectNavigate: () => false }).catch(() => {});
            setAccessDenied(true);
          }
        } catch (e) {
          try {
            const response = await msalInstance.loginPopup(loginRequest);
            if (!verifyAndSetRecruiter(response.account)) {
              await msalInstance.logoutPopup({ onRedirectNavigate: () => false }).catch(() => {});
              setAccessDenied(true);
            }
          } catch (loginErr) { setAccessDenied(true); }
        }
      }
    });
    return () => { if (stepIntervalRef.current) clearInterval(stepIntervalRef.current); };
  }, []);

  const handleLogout = () => { msalInstance.logoutPopup(); setRecruiter(null); setGeneratedLink(""); };
  const toggleLang = (lang) => { setSelectedLangs(prev => prev.includes(lang) ? prev.length > 1 ? prev.filter(l => l !== lang) : prev : [...prev, lang]); setGeneratedLink(""); };
  const set = k => e => { setF(p => ({ ...p, [k]: e.target.value })); setGeneratedLink(""); };
  const ok = f.name.trim() && f.email.trim() && f.role.trim() && f.companyName.trim() && f.companyWebsite.trim() && f.manager.trim() && f.managerEmail.trim() && recruiter && selectedLangs.length > 0;

  const handleGenerateLink = async () => {
    if (!ok) return;
    setGeneratingLink(true); setGeneratedCompanyName(""); setGeneratingStep(0);
    stepIntervalRef.current = setInterval(() => {
      setGeneratingStep(s => s < GENERATING_STEPS.length - 1 ? s + 1 : s);
    }, 3500);
    try {
      const testParams = { ...f, languages: selectedLangs, reportLang, testId: Math.random().toString(36).substring(2, 8).toUpperCase(), writingIdxMap: Object.fromEntries(selectedLangs.map(l => [l, Math.floor(Math.random() * 3)])) };
      const res = await fetch(SAVE_TEST_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ testParams }) });
      const data = await res.json();
      setGeneratedLink(data.shortUrl);
      if (data.companyName) setGeneratedCompanyName(data.companyName);
    } catch (e) { setGeneratedLink("Error generating link. Please try again."); }
    clearInterval(stepIntervalRef.current);
    setGeneratingLink(false); setGeneratingStep(0);
  };

  const params = ok ? { ...f, languages: selectedLangs, reportLang, testId: "PREVIEW", writingIdxMap: Object.fromEntries(selectedLangs.map(l => [l, Math.floor(Math.random() * 3)])) } : null;

  const retryLogin = async () => {
    setAccessDenied(false); setLoginError("");
    try {
      const response = await msalInstance.loginPopup(loginRequest);
      if (!verifyAndSetRecruiter(response.account)) {
        await msalInstance.logoutPopup({ onRedirectNavigate: () => false }).catch(() => {});
        setAccessDenied(true);
        setLoginError("Access denied. Please use your Axepta work account.");
      }
    } catch (e) { setAccessDenied(true); }
  };

  if (accessDenied) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
        <Fonts />
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ marginBottom: 24 }}><Logo /></div>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
          <h2 style={{ color: T.text, margin: "0 0 10px", fontSize: 20, fontWeight: 700 }}>Access Denied</h2>
          <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>This tool is only available to Axepta staff.</p>
          <p style={{ color: T.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>Please sign in with your Axepta work account.</p>
          {loginError && <div style={{ color: T.red, fontSize: 13, marginBottom: 16 }}>{loginError}</div>}
          <Btn onClick={retryLogin}>🏢 Sign in with Axepta account</Btn>
        </div>
      </div>
    );
  }

  if (!recruiter) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
        <Fonts />
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ marginBottom: 24 }}><Logo /></div>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <p style={{ color: T.muted, fontSize: 14 }}>Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: "100%", maxWidth: 520 }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}><Logo /><div style={{ color: T.dim, fontSize: 13, marginTop: 6 }}>AI-powered multilingual assessment for hiring</div></div>
        <Card>
          <h2 style={{ color: T.text, margin: "0 0 4px", fontSize: 19, fontWeight: 700 }}>Create candidate test</h2>
          <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13 }}>Select languages and generate a link for your candidate.</p>
          <div style={{ marginBottom: 20, background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.18)", borderRadius: 14, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ color: T.green, fontSize: 11, fontWeight: 600, marginBottom: 3, letterSpacing: 0.5, textTransform: "uppercase" }}>✓ Signed in as Recruiter</div>
              <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{recruiter.name}</div>
              <div style={{ color: T.muted, fontSize: 13 }}>{recruiter.email}</div>
            </div>
            <button onClick={handleLogout} style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: "6px 12px" }}>Sign out</button>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", color: T.muted, fontSize: 11, marginBottom: 10, letterSpacing: 0.5, textTransform: "uppercase" }}>Languages to assess</label>
            <div style={{ display: "flex", gap: 10 }}>
              {Object.entries(LANG_META).map(([lang, meta]) => {
                const selected = selectedLangs.includes(lang);
                return <button key={lang} onClick={() => toggleLang(lang)} style={{ flex: 1, padding: "12px 8px", borderRadius: 12, border: `2px solid ${selected ? T.purple : T.border}`, background: selected ? "rgba(123,97,255,0.12)" : "rgba(255,255,255,0.02)", color: selected ? T.purple : T.muted, fontFamily: "inherit", cursor: "pointer", fontWeight: selected ? 700 : 400, fontSize: 13, transition: "all 0.2s", textAlign: "center" }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{meta.flag}</div>
                  <div>{meta.label}</div>
                  {selected && <div style={{ fontSize: 10, marginTop: 3, color: T.green }}>✓ Selected</div>}
                </button>;
              })}
            </div>
            {selectedLangs.length > 1 && <div style={{ marginTop: 8, color: T.muted, fontSize: 12 }}>⏱ Estimated time: ~{selectedLangs.length * 8}–{selectedLangs.length * 10} minutes</div>}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", color: T.muted, fontSize: 11, marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>Report language</label>
            <div style={{ display: "flex", gap: 8 }}>
              {Object.entries(LANG_META).map(([lang, meta]) => (
                <button key={lang} onClick={() => setReportLang(lang)} style={{ flex: 1, padding: "10px 6px", borderRadius: 10, border: `2px solid ${reportLang === lang ? T.green : T.border}`, background: reportLang === lang ? "rgba(0,212,170,0.1)" : "rgba(255,255,255,0.02)", color: reportLang === lang ? T.green : T.muted, fontFamily: "inherit", cursor: "pointer", fontWeight: reportLang === lang ? 700 : 400, fontSize: 12, transition: "all 0.2s", textAlign: "center" }}>
                  {meta.flag} {meta.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Field label="Candidate Name" value={f.name} onChange={set("name")} placeholder="e.g. Sarah Johnson" />
            <Field label="Candidate Email" value={f.email} onChange={set("email")} placeholder="e.g. sarah@email.com" />
            <Field label="Role Applied For" value={f.role} onChange={set("role")} placeholder="e.g. Customer Success Manager" />
            <Field label="Company Name" value={f.companyName} onChange={set("companyName")} placeholder="e.g. Axepta" />
            <Field label="Company Website" value={f.companyWebsite} onChange={set("companyWebsite")} placeholder="e.g. axepta.com or microsoft.com" />
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 13, display: "flex", flexDirection: "column", gap: 13 }}>
              <Field label="Recruiter Name" value={f.manager} onChange={set("manager")} placeholder="" locked={!!recruiter} />
              <Field label="Recruiter Email" value={f.managerEmail} onChange={set("managerEmail")} placeholder="" locked={!!recruiter} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
            <Btn onClick={() => onPreview(params)} disabled={!ok} variant="purple" style={{ flex: 1 }}>👁 Preview</Btn>
            {generatingLink ? (
              <div style={{ flex: 2, background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.2)", borderRadius: 12, padding: "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 16, height: 16, border: `2px solid ${T.green}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                  <div style={{ color: T.green, fontSize: 13, fontWeight: 600 }}>{GENERATING_STEPS[generatingStep].icon} {GENERATING_STEPS[generatingStep].text}</div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 99, height: 4, overflow: "hidden" }}>
                  <div style={{ width: `${((generatingStep + 1) / GENERATING_STEPS.length) * 100}%`, height: "100%", background: T.green, borderRadius: 99, transition: "width 3.5s ease" }} />
                </div>
                <div style={{ color: T.dim, fontSize: 11, marginTop: 8, textAlign: "center" }}>AI is reading the company website — this takes ~20 seconds</div>
              </div>
            ) : (
              <Btn onClick={handleGenerateLink} disabled={!ok} style={{ flex: 2 }}>🔗 Generate Candidate Link</Btn>
            )}
          </div>

          {generatedLink && !generatedLink.includes("Error") && (
            <div style={{ marginTop: 14, background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.18)", borderRadius: 12, padding: "14px 16px" }}>
              {generatedCompanyName && (
                <div style={{ marginBottom: 10, padding: "8px 12px", background: "rgba(123,97,255,0.1)", border: `1px solid rgba(123,97,255,0.25)`, borderRadius: 8 }}>
                  <div style={{ color: T.purple, fontSize: 11, fontWeight: 600, marginBottom: 3, letterSpacing: 0.5, textTransform: "uppercase" }}>✓ Company content generated</div>
                  <div style={{ color: "#ccc", fontSize: 12 }}>{generatedCompanyName} — prompts tailored to company values and {f.role} role</div>
                </div>
              )}
              <div style={{ color: T.green, fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>📎 Candidate link ready for {f.name}</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <div style={{ flex: 1, color: T.text, fontSize: 13, wordBreak: "break-all", fontFamily: "'DM Mono', monospace", lineHeight: 1.5 }}>{generatedLink}</div>
                <button onClick={() => { navigator.clipboard.writeText(generatedLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(0,212,170,0.35)", background: "transparent", color: T.green, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{copied ? "✓" : "Copy"}</button>
              </div>
              <button onClick={() => {
                const draft = buildHtmlDraftEmail({ ...f, languages: selectedLangs, companyName: f.companyName || generatedCompanyName }, generatedLink);
                const container = document.createElement("div");
                container.innerHTML = draft.html;
                container.style.position = "fixed"; container.style.left = "-9999px"; container.style.top = "0"; container.style.width = "700px"; container.style.background = "white";
                document.body.appendChild(container);
                const range = document.createRange(); range.selectNode(container);
                const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
                try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 3000); } catch (e) { console.error("Copy failed", e); }
                selection.removeAllRanges(); document.body.removeChild(container);
              }} style={{ width: "100%", padding: "13px 20px", borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${T.purple}, #5a46cc)`, color: "#fff", fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                {copied ? "✅ Copied! Open Outlook → New Email → Ctrl+V" : "📋 Copy Invitation Email"}
              </button>
              <div style={{ marginTop: 8, color: T.muted, fontSize: 11, textAlign: "center" }}>Copies formatted HTML email → open Outlook → New Email → paste with Ctrl+V</div>
            </div>
          )}
          {generatedLink && generatedLink.includes("Error") && <div style={{ marginTop: 14, color: T.red, fontSize: 13, background: "rgba(255,107,107,0.08)", padding: "10px 14px", borderRadius: 8 }}>{generatedLink}</div>}
        </Card>
        <div style={{ marginTop: 18, display: "flex", gap: 20, justifyContent: "center" }}>
          {["🔒 GDPR Compliant", "🌍 EN / FR / DE", "📊 AI analysis"].map(t => <div key={t} style={{ color: T.dim, fontSize: 12 }}>{t}</div>)}
        </div>
      </div>
    </div>
  );
}

function Welcome({ params, onStart }) {
  const languages = params.languages || ["en"];
  const uiLang = languages[0];
  const ui = WELCOME_UI[uiLang] || WELCOME_UI.en;
  const totalMins = languages.length * 8;

  useEffect(() => {
    if (window.SpeechSDK) { getSpeechToken().catch(() => {}); return; }
    const check = setInterval(() => {
      if (window.SpeechSDK) { clearInterval(check); getSpeechToken().catch(() => {}); }
    }, 500);
    return () => clearInterval(check);
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ width: "100%", maxWidth: 560 }}>
        <Card>
          <div style={{ textAlign: "center", marginBottom: 26 }}>
            <div style={{ fontSize: 50, marginBottom: 12 }}>👋</div>
            <h1 style={{ margin: "0 0 10px", fontSize: 24, fontWeight: 800, color: T.text }}>{ui.hi}, {params.name}!</h1>
            <p style={{ color: T.muted, margin: "0 0 14px", fontSize: 14, lineHeight: 1.7 }}>{ui.subtitle} <strong style={{ color: T.text }}>{params.role}</strong> {ui.position}</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>{languages.map(l => <LangBadge key={l} lang={l} />)}</div>
          </div>
          {languages.length > 1 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ color: T.muted, fontSize: 12, marginBottom: 10, textAlign: "center" }}>{ui.order}</div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                {languages.map((l, i) => <div key={l} style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ background: "rgba(123,97,255,0.1)", border: `1px solid ${T.purple}30`, borderRadius: 10, padding: "8px 14px", fontSize: 13, color: T.purple }}>{LANG_META[l].flag} {LANG_META[l].label}</div>{i < languages.length - 1 && <span style={{ color: T.dim }}>→</span>}</div>)}
              </div>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 22 }}>
            {[["✍️", ui.writing, ui.writingDesc, T.yellow], ["🎙", ui.speaking, ui.speakingDesc, T.purple], ["👂", ui.listening, ui.listeningDesc, T.green]].map(([icon, title, desc, color]) => (
              <div key={title} style={{ background: `${color}08`, border: `1px solid ${color}25`, borderRadius: 14, padding: "16px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{title}</div>
                <div style={{ color: T.muted, fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>{desc}</div>
              </div>
            ))}
          </div>
          <div style={{ background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.18)", borderRadius: 12, padding: "14px 16px", marginBottom: 14, fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
            ⏱ {ui.time}: <strong style={{ color: T.text }}>~{totalMins} {ui.minutes}</strong>. {ui.quiet}
          </div>
          <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 22, fontSize: 12, color: T.muted, lineHeight: 1.7 }}>
            🤖 {ui.aiDisclaimer}
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: T.muted, fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>💡 {ui.beforeWeBegin}</div>
            <Btn onClick={onStart}>{ui.startBtn}</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Writing({ lang, writingIdx, languages, langIdx, onNext, customContent, isFirst }) {
  const [val, setVal] = useState("");
  const [timedOut, setTimedOut] = useState(false);
  const c = CONTENT[lang];
  const ui = c.ui;
  const customLangContent = customContent?.[lang];
  const prompt = customLangContent?.writingPrompts?.[writingIdx] ?? c.writingPrompts[writingIdx];
  const words = val.trim().split(/\s+/).filter(Boolean).length;
  const timeLimit = isFirst ? WRITING_TIME_FIRST : WRITING_TIME;
  const handleExpire = useCallback(() => setTimedOut(true), []);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ width: "100%", maxWidth: 640 }}>
        <SessionProgress languages={languages} langIdx={langIdx} section="writing" />
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <SectionHead icon="✍️" title={ui.writingTitle} sub={`${ui.writingSub} · ${isFirst ? "4" : "3"} minutes`} color={T.yellow} />
            <LangBadge lang={lang} />
          </div>
          <CountdownTimer key={timeLimit} seconds={timeLimit} onExpire={handleExpire} color={T.yellow} />
          {timedOut && <div style={{ background: "rgba(255,107,107,0.1)", border: "1px solid rgba(255,107,107,0.3)", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}><div style={{ color: T.red, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{ui.timesUp}</div><div style={{ color: "#ccc", fontSize: 13 }}>{ui.timesUpMsg}</div></div>}
          <div style={{ background: `${T.yellow}0a`, border: `1px solid ${T.yellow}28`, borderRadius: 12, padding: "18px 20px", marginBottom: 18 }}>
            <div style={{ color: T.yellow, fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>{ui.writingLabel}</div>
            <p style={{ margin: 0, color: "#ddd", fontSize: 15, lineHeight: 1.75 }}>{prompt}</p>
          </div>
          <textarea value={val} onChange={e => { if (!timedOut) setVal(e.target.value); }} placeholder={timedOut ? "" : ui.writingPlaceholder} disabled={timedOut} {...antiCheatProps()}
            style={{ width: "100%", minHeight: 160, padding: 15, boxSizing: "border-box", background: timedOut ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)", border: `1px solid ${timedOut ? "rgba(255,107,107,0.3)" : "rgba(255,255,255,0.1)"}`, borderRadius: 12, color: timedOut ? T.muted : T.text, fontSize: 14, fontFamily: "inherit", lineHeight: 1.75, resize: "vertical", outline: "none", cursor: timedOut ? "not-allowed" : "text" }}
            onFocus={e => { if (!timedOut) e.target.style.borderColor = T.yellow; }}
            onBlur={e => { if (!timedOut) e.target.style.borderColor = "rgba(255,255,255,0.1)"; }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
            <div style={{ color: T.muted, fontSize: 13, fontFamily: "'DM Mono', monospace" }}>{ui.writingWords(words)}</div>
            <Btn onClick={() => onNext(val)} disabled={!timedOut && words < 5}>{ui.writingNext}</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Speaking({ lang, languages, langIdx, onNext, customContent }) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("reading");
  const [transcripts, setTranscripts] = useState(["", "", ""]);
  const [timerKey, setTimerKey] = useState(0);
  const [error, setError] = useState("");
  const recognizerRef = useRef(null);
  const transcriptRef = useRef("");
  const c = CONTENT[lang];
  const ui = c.ui;
  const customLangContent = customContent?.[lang];
  const prompt = customLangContent?.speakingPrompts?.[idx] ?? c.speakingPrompts[idx];
  const azureLang = LANG_META[lang].azureLang;

  const stopRec = useCallback(async () => {
    if (recognizerRef.current) {
      await stopAzureRecognition(recognizerRef.current);
      recognizerRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    setPhase("recording");
    transcriptRef.current = "";
    setError("");
    const recognizer = await startAzureRecognition(
      azureLang,
      (text) => {
        transcriptRef.current += text + " ";
        setTranscripts(p => { const n = [...p]; n[idx] = transcriptRef.current; return n; });
      },
      (err) => { setError(ui.micError + " (" + err + ")"); }
    );
    if (recognizer) recognizerRef.current = recognizer;
  }, [idx, ui, azureLang]);

  const handleReadExpire = useCallback(() => { startRecording(); }, [startRecording]);
  const handleRecordExpire = useCallback(async () => { await stopRec(); setPhase("done"); }, [stopRec]);

  const goNext = async () => {
    await stopRec();
    if (idx < 2) { setIdx(idx + 1); setPhase("reading"); setTimerKey(k => k + 1); }
    else { onNext(transcripts); }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <style>{`@keyframes ripple{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}`}</style>
      <div style={{ width: "100%", maxWidth: 620 }}>
        <SessionProgress languages={languages} langIdx={langIdx} section="speaking" />
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <SectionHead icon="🎙" title={ui.speakingTitle(idx)} sub={ui.speakingSub} color={T.purple} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
              <LangBadge lang={lang} />
              <div style={{ display: "flex", gap: 6 }}>{[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i < idx ? T.green : i === idx ? T.purple : "rgba(255,255,255,0.1)" }} />)}</div>
            </div>
          </div>
          {phase === "reading" && <CountdownTimer key={`read-${timerKey}-${idx}`} seconds={SPEAKING_READ_TIME} onExpire={handleReadExpire} color={T.yellow} label={`⏱ ${ui.readingTimer}`} />}
          {phase === "recording" && <CountdownTimer key={`rec-${timerKey}-${idx}`} seconds={SPEAKING_RECORD_TIME} onExpire={handleRecordExpire} color={T.purple} label={`🔴 ${ui.recordingTimer}`} />}
          <div style={{ background: `${T.purple}0a`, border: `1px solid ${phase === "reading" ? T.yellow + "60" : T.purple + "40"}`, borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
            <div style={{ color: phase === "reading" ? T.yellow : T.purple, fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>{ui.speakingLabel}</div>
            <p style={{ margin: 0, color: "#ddd", fontSize: 14, lineHeight: 1.75 }}>{prompt.text}</p>
          </div>
          {phase === "reading" && <div style={{ textAlign: "center", padding: "8px 0 16px" }}><div style={{ color: T.yellow, fontSize: 13, marginBottom: 4 }}>📖 {ui.readingTimer}</div><div style={{ color: T.muted, fontSize: 12 }}>Recording will start automatically when the timer expires</div></div>}
          {phase === "recording" && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", margin: "0 auto 12px", background: T.purple, border: "3px solid #ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, animation: "ripple 1.5s infinite" }}>🎙</div>
              <div style={{ color: T.purple, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{ui.autoRecording}</div>
              <Btn onClick={async () => { await stopRec(); setPhase("done"); }} variant="danger" style={{ fontSize: 13, padding: "10px 20px" }}>{ui.stopRec}</Btn>
            </div>
          )}
          {phase === "done" && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", margin: "0 auto 12px", background: T.green, border: "3px solid #ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>✅</div>
              <div style={{ color: T.green, fontSize: 13, fontWeight: 600 }}>{ui.recordingComplete}</div>
            </div>
          )}
          {error && <div style={{ color: T.red, fontSize: 13, background: "rgba(255,107,107,0.08)", padding: "10px 14px", borderRadius: 8, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn onClick={goNext} disabled={phase === "reading" || phase === "recording"}>{idx < 2 ? ui.nextPrompt : ui.nextListening}</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Listening({ lang, languages, langIdx, isLast, onSubmit, customContent }) {
  const [phase, setPhase] = useState("ready");
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState("");
  const [timedOut, setTimedOut] = useState(false);
  const intRef = useRef(null);
  const c = CONTENT[lang];
  const ui = c.ui;
  const customLangContent = customContent?.[lang];
  const passage = customLangContent?.listeningPassage ?? c.listeningPassage;
  const voiceLang = LANG_META[lang].voiceLang;

  const play = () => {
    setPhase("playing"); setProgress(0);
    window.speechSynthesis.cancel();

    const getBestVoice = (lang) => {
      const voices = window.speechSynthesis.getVoices();
      const langCode = lang.split("-")[0];
      const preferredByLang = {
        en: ["Google UK English Female", "Microsoft Libby", "Microsoft Sonia", "Microsoft Hazel", "Microsoft Zira", "Samantha", "Karen", "Moira", "Tessa", "Fiona", "Victoria"],
        fr: ["Google français", "Microsoft Denise", "Microsoft Hortense", "Amelie"],
        de: ["Google Deutsch", "Microsoft Katja", "Microsoft Hedda", "Anna"],
      };
      const preferred = preferredByLang[langCode] || preferredByLang.en;
      for (const name of preferred) { const v = voices.find(v => v.name.includes(name) && v.lang.startsWith(langCode)); if (v) return v; }
      const female = voices.filter(v => v.lang.startsWith(langCode) && /female|woman/i.test(v.name));
      if (female.length > 0) return female[0];
      return voices.find(v => v.lang.startsWith(langCode)) || null;
    };

    const speakPassage = () => {
      const wordCount = passage.text.split(/\s+/).length;
      const estimatedMs = Math.max(10000, (wordCount / 0.85) * 60 * 1000 / 130);
      let elapsed = 0;
      intRef.current = setInterval(() => { elapsed += 100; setProgress(Math.min(99, (elapsed / estimatedMs) * 100)); }, 100);
      const utt = new SpeechSynthesisUtterance(passage.text);
      utt.rate = 0.85; utt.volume = 0.65; utt.lang = voiceLang;
      const voice = getBestVoice(voiceLang);
      if (voice) utt.voice = voice;
      utt.onend = () => { clearInterval(intRef.current); setProgress(100); setTimeout(() => setPhase("questions"), 600); };
      utt.onerror = () => { clearInterval(intRef.current); setProgress(100); setTimeout(() => setPhase("questions"), 600); };
      window.speechSynthesis.speak(utt);
    };

    let spoken = false;
    const trySpeak = () => { if (spoken) return; spoken = true; speakPassage(); };
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) { trySpeak(); } else { window.speechSynthesis.onvoiceschanged = () => { trySpeak(); }; }
  };

  const handleExpire = useCallback(() => setTimedOut(true), []);
  const canSubmit = summary.trim().split(/\s+/).filter(Boolean).length >= 5 || timedOut;

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ width: "100%", maxWidth: 600 }}>
        <SessionProgress languages={languages} langIdx={langIdx} section="listening" />
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <SectionHead icon="👂" title={ui.listeningTitle} sub={ui.listeningSub} color={T.green} />
            <LangBadge lang={lang} />
          </div>
          {phase === "ready" && (
            <div style={{ textAlign: "center" }}>
              <p style={{ color: T.muted, fontSize: 14, marginBottom: 24, lineHeight: 1.7 }}>{ui.listeningIntro}</p>
              <div style={{ background: `${T.green}08`, border: `1px solid ${T.green}25`, borderRadius: 16, padding: 32, marginBottom: 24 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🔊</div>
                <div style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>{ui.listeningDuration}</div>
                <Btn onClick={play}>{ui.playBtn}</Btn>
              </div>
            </div>
          )}
          {phase === "playing" && (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>🔊</div>
              <div style={{ color: T.green, fontWeight: 600, marginBottom: 16, fontSize: 15 }}>{ui.listeningProgress}</div>
              <ProgressBar value={progress} max={100} color={T.green} />
              <div style={{ color: T.muted, fontSize: 12, marginTop: 8 }}>{ui.listeningWait}</div>
            </div>
          )}
          {phase === "questions" && (
            <div>
              <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, color: T.muted, fontSize: 13 }}>{ui.listeningDone}</div>
              <CountdownTimer seconds={LISTENING_SUMMARY_TIME} onExpire={handleExpire} color={T.green} />
              {timedOut && <div style={{ background: "rgba(255,107,107,0.1)", border: "1px solid rgba(255,107,107,0.3)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}><div style={{ color: T.red, fontSize: 13 }}>{ui.timesUp} {ui.timesUpMsg}</div></div>}
              <div style={{ background: `${T.green}0a`, border: `1px solid ${T.green}28`, borderRadius: 12, padding: "14px 18px", marginBottom: 16 }}>
                <div style={{ color: T.green, fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>Summary Question</div>
                <p style={{ margin: 0, color: "#ddd", fontSize: 14, lineHeight: 1.75 }}>{passage.summaryQuestion}</p>
              </div>
              <textarea value={summary} onChange={e => { if (!timedOut) setSummary(e.target.value); }} placeholder={timedOut ? "" : ui.summaryPlaceholder} disabled={timedOut} {...antiCheatProps()}
                style={{ width: "100%", minHeight: 140, padding: 14, boxSizing: "border-box", background: timedOut ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.05)", border: `1px solid ${timedOut ? "rgba(255,107,107,0.3)" : "rgba(255,255,255,0.1)"}`, borderRadius: 10, color: timedOut ? T.muted : T.text, fontSize: 14, fontFamily: "inherit", lineHeight: 1.7, resize: "vertical", outline: "none", cursor: timedOut ? "not-allowed" : "text" }}
                onFocus={e => { if (!timedOut) e.target.style.borderColor = T.green; }}
                onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                <div style={{ color: T.muted, fontSize: 13, fontFamily: "'DM Mono', monospace" }}>{summary.trim().split(/\s+/).filter(Boolean).length} words</div>
                <Btn onClick={() => onSubmit(summary)} disabled={!canSubmit}>{ui.submitBtn}</Btn>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Processing() {
  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign: "center", maxWidth: 400, padding: 24 }}>
        <div style={{ fontSize: 60, marginBottom: 20, display: "inline-block", animation: "spin 3s linear infinite" }}>⚙️</div>
        <h2 style={{ color: T.text, margin: "0 0 8px", fontSize: 22, fontWeight: 700 }}>Analysing your responses…</h2>
        <p style={{ color: T.muted, fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>Please wait while we process your assessment.</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
          {["Grammar & vocabulary", "Fluency & coherence", "Comprehension", "Generating report"].map(s => <div key={s} style={{ color: T.dim, fontSize: 12, background: "rgba(255,255,255,0.04)", padding: "6px 12px", borderRadius: 99 }}>{s}</div>)}
        </div>
      </div>
    </div>
  );
}

function Report({ results, params, onBack }) {
  const [activeLang, setActiveLang] = useState(results.languages[0].lang);
  const cols = { Writing: T.yellow, Speaking: T.purple, Listening: T.green };
  const recColor = results.overallRecommendation?.toLowerCase().includes("not") ? T.red : results.overallRecommendation?.toLowerCase().includes("reserv") ? T.yellow : T.green;
  const langResult = results.languages.find(l => l.lang === activeLang) || results.languages[0];
  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, padding: "40px 24px", fontFamily: "'Sora', sans-serif" }}>
      <Fonts />
      <div style={{ maxWidth: 660, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <div style={{ color: T.dim, fontSize: 12, marginBottom: 4 }}>LinguaCheck · Confidential Report · Preview</div>
            <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 800 }}>{params?.name}</h1>
            <div style={{ color: T.muted, fontSize: 13, marginBottom: 8 }}>Applying for: <strong style={{ color: "#ccc" }}>{params?.role}</strong></div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{results.languages.map(l => <LangBadge key={l.lang} lang={l.lang} />)}</div>
          </div>
          {onBack && <button onClick={onBack} style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: "8px 14px" }}>← New test</button>}
        </div>
        <div style={{ background: `${recColor}0d`, border: `1px solid ${recColor}30`, borderRadius: 16, padding: "18px 20px", marginBottom: 20 }}>
          <div style={{ color: recColor, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>⚡ {results.overallRecommendation}</div>
          <p style={{ margin: 0, color: "#ccc", fontSize: 14, lineHeight: 1.75 }}>{results.summaryForRecruiter}</p>
        </div>
        {results.languages.length > 1 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {results.languages.map(l => <button key={l.lang} onClick={() => setActiveLang(l.lang)} style={{ padding: "10px 18px", borderRadius: 12, border: `2px solid ${activeLang === l.lang ? T.purple : T.border}`, background: activeLang === l.lang ? "rgba(123,97,255,0.12)" : "transparent", color: activeLang === l.lang ? T.purple : T.muted, fontFamily: "inherit", cursor: "pointer", fontWeight: activeLang === l.lang ? 700 : 400, fontSize: 13 }}>{LANG_META[l.lang].flag} {LANG_META[l.lang].label} · <span style={{ fontFamily: "'DM Mono', monospace" }}>{l.cefrLevel}</span></button>)}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, marginBottom: 14 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.green}35`, borderRadius: 16, padding: 24, textAlign: "center" }}>
            <div style={{ color: T.muted, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>CEFR Level</div>
            <div style={{ fontSize: 10, color: T.muted, marginBottom: 8 }}>{LANG_META[activeLang].flag} {LANG_META[activeLang].label}</div>
            <div style={{ fontSize: 52, fontWeight: 800, color: T.green, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{langResult.cefrLevel}</div>
          </div>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
            <div style={{ color: T.muted, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Overall Score</div>
            <div style={{ fontSize: 44, fontWeight: 800, fontFamily: "'DM Mono', monospace", marginBottom: 10 }}>{langResult.overallScore}<span style={{ fontSize: 18, color: T.dim }}>/100</span></div>
            <ProgressBar value={langResult.overallScore} max={100} color={T.green} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
          {[["Writing", langResult.writingScore], ["Speaking", langResult.speakingScore], ["Listening", langResult.listeningScore]].map(([label, score]) => (
            <div key={label} style={{ background: T.surface, border: `1px solid ${cols[label]}22`, borderRadius: 14, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: cols[label], fontFamily: "'DM Mono', monospace" }}>{score}</div>
              <div style={{ fontSize: 11, color: T.muted, margin: "4px 0 10px", letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
              <ProgressBar value={score} max={100} color={cols[label]} />
            </div>
          ))}
        </div>
        {[["Writing", langResult.writingFeedback], ["Speaking", langResult.speakingFeedback], ["Listening", langResult.listeningFeedback]].map(([label, text]) => (
          <div key={label} style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 20px", marginBottom: 10 }}>
            <div style={{ color: cols[label], fontWeight: 600, fontSize: 11, marginBottom: 8, letterSpacing: 0.5, textTransform: "uppercase" }}>{label} Feedback</div>
            <p style={{ margin: 0, color: "#ccc", fontSize: 14, lineHeight: 1.75 }}>{text}</p>
          </div>
        ))}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
          <div style={{ background: "rgba(0,212,170,0.04)", border: "1px solid rgba(0,212,170,0.18)", borderRadius: 14, padding: "16px 20px" }}>
            <div style={{ color: T.green, fontWeight: 600, fontSize: 11, marginBottom: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>Strengths</div>
            {langResult.strengths?.map((s, i) => <div key={i} style={{ color: "#ccc", fontSize: 13, marginBottom: 8, paddingLeft: 12, borderLeft: `2px solid ${T.green}40` }}>✓ {s}</div>)}
          </div>
          <div style={{ background: "rgba(255,107,107,0.04)", border: "1px solid rgba(255,107,107,0.18)", borderRadius: 14, padding: "16px 20px" }}>
            <div style={{ color: "#ff9999", fontWeight: 600, fontSize: 11, marginBottom: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>Areas to Improve</div>
            {langResult.areasForImprovement?.map((a, i) => <div key={i} style={{ color: "#ccc", fontSize: 13, marginBottom: 8, paddingLeft: 12, borderLeft: "2px solid rgba(255,107,107,0.3)" }}>→ {a}</div>)}
          </div>
        </div>
        <div style={{ textAlign: "center", color: T.dim, fontSize: 12 }}>Confidential · Candidate data deleted after analysis · GDPR Compliant · LinguaCheck AI Assessment</div>
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("loading");
  const [params, setParams] = useState(null);
  const [langIdx, setLangIdx] = useState(0);
  const [section, setSection] = useState("writing");
  const [langResults, setLangResults] = useState([]);
  const [currentLangData, setCurrentLangData] = useState({ writingResponse: "", speakingTranscripts: ["", "", ""], listeningSummary: "" });
  const [results, setResults] = useState(null);
  const [err, setErr] = useState("");
  const [loadError, setLoadError] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  const [writingCount, setWritingCount] = useState(0);

  const languages = params?.languages || ["en"];
  const currentLang = languages[langIdx];
  const isLastLang = langIdx === languages.length - 1;
  const uiLang = params?.languages?.[0] || "en";

  useEffect(() => {
    if (!window.SpeechSDK) {
      const script = document.createElement("script");
      script.src = "https://aka.ms/csspeech/jsbrowserpackageraw";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    const shortIdMatch = path.match(/^\/t\/([A-Z0-9]+)$/i);
    if (shortIdMatch) {
      const shortId = shortIdMatch[1].toUpperCase();
      fetch(`${GET_TEST_URL}&id=${shortId}`)
        .then(res => res.json())
        .then(data => {
          if (data.testParams) { setParams(data.testParams); setScreen("welcome"); }
          else { setLoadError(data.error || "Test not found."); setScreen("error"); }
        })
        .catch(() => { setLoadError("Unable to load your test. Please check your link or contact the recruiter."); setScreen("error"); });
    } else { setScreen("setup"); }
  }, []);

  const handleWritingDone = (writingResponse) => { setCurrentLangData(d => ({ ...d, writingResponse })); setWritingCount(c => c + 1); setSection("speaking"); };
  const handleSpeakingDone = (speakingTranscripts) => { setCurrentLangData(d => ({ ...d, speakingTranscripts })); setSection("listening"); };

  const handleListeningDone = async (listeningSummary) => {
    const completedLang = {
      lang: currentLang,
      writingIdx: params.writingIdxMap[currentLang],
      writingResponse: currentLangData.writingResponse,
      speakingTranscripts: currentLangData.speakingTranscripts,
      listeningSummary,
      customContent: params.customContent || null,
    };
    const allLangResults = [...langResults, completedLang];

    if (!isLastLang) {
      setLangResults(allLangResults); setScreen("langbreak");
    } else {
      setScreen("processing"); setErr("");
      try {
        const r = await analyzeWithClaude(allLangResults, params.reportLang || "en", params.name, params.role);
        sendReport(r, params).catch(e => console.error("Email send failed:", e));
        if (isPreview) { setResults(r); setScreen("report"); } else { setScreen("thankyou"); }
      } catch (e) { setErr("Analysis failed. Please check your connection and try again."); setSection("listening"); setScreen("test"); }
    }
  };

  const handleLangBreakProceed = () => {
    setLangIdx(langIdx + 1);
    setCurrentLangData({ writingResponse: "", speakingTranscripts: ["", "", ""], listeningSummary: "" });
    setSection("writing"); setScreen("test");
  };

  const handlePreview = (p) => {
    setParams(p); setIsPreview(true); setLangIdx(0); setLangResults([]);
    setCurrentLangData({ writingResponse: "", speakingTranscripts: ["", "", ""], listeningSummary: "" });
    setSection("writing"); setWritingCount(0); setScreen("welcome");
  };

  if (screen === "loading") return <LoadingScreen message="Loading..." />;
  if (screen === "error") return <ErrorScreen message={loadError} />;
  if (screen === "setup") return <Setup onPreview={handlePreview} />;
  if (screen === "welcome") return <Welcome params={params} onStart={() => setScreen("devicecheck")} />;
  if (screen === "devicecheck") return <DeviceCheck uiLang={uiLang} onPassed={() => setScreen("consent")} />;
  if (screen === "consent") return <ConsentScreen params={params} onConsented={(updatedParams) => { setParams(updatedParams); setScreen("test"); }} />;
  if (screen === "langbreak") return <LangBreakScreen nextLang={languages[langIdx + 1]} uiLang={uiLang} onProceed={handleLangBreakProceed} />;
  if (screen === "thankyou") return <ThankYouScreen params={params} />;

  if (screen === "test") {
    const writingIdx = params?.writingIdxMap?.[currentLang] ?? 0;
    const customContent = params?.customContent || null;
    return (
      <>
        {err && <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", background: T.red, color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, zIndex: 9999, fontFamily: "Sora, sans-serif" }}>{err}</div>}
        {section === "writing" && <Writing lang={currentLang} writingIdx={writingIdx} languages={languages} langIdx={langIdx} onNext={handleWritingDone} customContent={customContent} isFirst={writingCount === 0} />}
        {section === "speaking" && <Speaking lang={currentLang} languages={languages} langIdx={langIdx} onNext={handleSpeakingDone} customContent={customContent} />}
        {section === "listening" && <Listening lang={currentLang} languages={languages} langIdx={langIdx} isLast={isLastLang} onSubmit={handleListeningDone} customContent={customContent} />}
      </>
    );
  }

  if (screen === "processing") return <Processing />;
  if (screen === "report") return <Report results={results} params={params} onBack={() => { setScreen("setup"); setResults(null); setLangIdx(0); setLangResults([]); setIsPreview(false); setWritingCount(0); }} />;
  return null;
}